/**
 * The wire contract between the ASHPORT Worker and the ASHPORT SPA.
 *
 * Imported by both sides. Nothing here is optional-by-accident: if a field can be absent it is
 * `| null`, never `?`, so a missing field is a type error and not a silent `undefined`.
 *
 * Normative source: docs/DATA-MODEL.md section 7.3. Every enum below is the count of artworks
 * that exist in art/manifest.json; see DATA-MODEL.md Appendix A for the audit.
 */

/* ------------------------------------------------------------------ primitives */

export type DistrictId =
  | 'bell_foundry'
  | 'north_quay'
  | 'slaughterhouse_flats'
  | 'ironbound'
  | 'verity_hill'
  | 'little_sicily'
  | 'harbor_ward'
  | 'saint_brigid'
  | 'cinder_park'
  | 'dockside'
  | 'kepler_row'
  | 'south_docks'

export const DISTRICT_IDS = [
  'bell_foundry',
  'north_quay',
  'slaughterhouse_flats',
  'ironbound',
  'verity_hill',
  'little_sicily',
  'harbor_ward',
  'saint_brigid',
  'cinder_park',
  'dockside',
  'kepler_row',
  'south_docks',
] as const satisfies readonly DistrictId[]

/** The six districts that ship an authored job chain in Era 1. The other six are Crime Districts. */
export const CHAINED_DISTRICT_IDS = [
  'dockside',
  'ironbound',
  'little_sicily',
  'verity_hill',
  'slaughterhouse_flats',
  'kepler_row',
] as const satisfies readonly DistrictId[]

export type ChainedDistrictId = (typeof CHAINED_DISTRICT_IDS)[number]

export type Rank = 'Associate' | 'Soldier' | 'Capo' | 'Underboss' | 'Boss'
export const RANKS = ['Associate', 'Soldier', 'Capo', 'Underboss', 'Boss'] as const satisfies readonly Rank[]
export const rankIndex = (r: Rank): number => RANKS.indexOf(r)

/**
 * THE LINE AT SOLDIER, Phase 24. One choice, made once an Era, when a player makes Soldier: the
 * Shift (a longer Energy bar) or the Edge (more Nerve). Empty until drawn. DESIGN-DELTAS 1ae.1.
 */
export type Line = '' | 'energy' | 'nerve'
export const LINES = ['energy', 'nerve'] as const satisfies readonly Exclude<Line, ''>[]
/** What each side is called, in voice, everywhere it is printed. */
export const LINE_NAME: Record<Exclude<Line, ''>, string> = { energy: 'the Shift', nerve: 'the Edge' }
/** The one word, for a cell that has room for one word: the top bar's identity cell. */
export const LINE_SHORT: Record<Exclude<Line, ''>, string> = { energy: 'Shift', nerve: 'Edge' }

export type HeatBand = 'calm' | 'watched' | 'dangerous' | 'critical' | 'rico'
export type Risk = 'low' | 'medium' | 'high' | 'extreme'
export type Currency = 'dirty' | 'clean'

/** SIX tiers, because market.rarity has six plates. */
export type Rarity = 'common' | 'uncommon' | 'rare' | 'exceptional' | 'epic' | 'legendary'
export const RARITIES = [
  'common',
  'uncommon',
  'rare',
  'exceptional',
  'epic',
  'legendary',
] as const satisfies readonly Rarity[]

/**
 * EIGHT roles, one per `families.roles.*` plate. Five ranks and three appointed posts.
 *
 * There is no `lieutenant`. It used to be here on the strength of a DATA-MODEL 3.2 note calling
 * this "the union of families.roles (8 plates) and characters.ranks (6 silhouettes)", and three
 * other sources say otherwise:
 *
 *   - GAME-DESIGN 8.2: "Eight `families.roles.*` assets. Five are ranks. Three are appointed
 *     posts, one holder each." Five plus three is eight and there is no ninth.
 *   - GAME-DESIGN 6.1 and CONTENT-BIBLE 3.5 both map `characters.ranks.lieutenant_silhouette` to
 *     UNDERBOSS. CONTENT-BIBLE says it outright: "The lieutenant silhouette carries Underboss
 *     because the art is right and the label is ours to set."
 *   - `art/manifest.json` has no `families.roles.lieutenant`, so `Member.roleArtId` had no plate
 *     to resolve for the ninth value and `A()` would have silently borrowed a sibling.
 *
 * A silhouette is not a role. Recorded in docs/DESIGN-DELTAS.md 1i.1.
 */
export type FamilyRole =
  | 'boss'
  | 'underboss'
  | 'consigliere'
  | 'capo'
  | 'enforcer'
  | 'driver'
  | 'soldier'
  | 'associate'

/**
 * The eight roles as data, so the enum, the validator and the SQL CHECK cannot drift apart.
 *
 * The Zod-enum-versus-CHECK-constraint hole has already cost this project once: `CosmeticSlot`
 * invented three slot names the database would have rejected on the first grant, and 46 of 52
 * cosmetics passed every gate on the way to that failure. Every list below is derived from this
 * one, and `server/test/family.test.ts` reads the CHECK back out of `sqlite_master` and compares.
 */
export const FAMILY_ROLES = [
  'boss',
  'underboss',
  'consigliere',
  'capo',
  'enforcer',
  'driver',
  'soldier',
  'associate',
] as const satisfies readonly FamilyRole[]

/** The five seats on the ladder. One Boss, one Underboss, up to five Capos, then the rest. */
export const FAMILY_RANK_ROLES = ['boss', 'underboss', 'capo', 'soldier', 'associate'] as const

/** The three appointed posts, one holder each, whose power scales off the holder's own record. */
export const FAMILY_POST_ROLES = ['consigliere', 'enforcer', 'driver'] as const
export type FamilyPost = (typeof FAMILY_POST_ROLES)[number]

/** Roles that sit inside a Capo's crew. Boss, Underboss and the three posts are crewless. */
export const FAMILY_CREWED_ROLES = ['capo', 'soldier', 'associate'] as const

/**
 * The granular permission grants from GAME-DESIGN 8.2, assignable by the Boss and audit-logged.
 *
 * Deliberately separate from `FamilyRole`: a role is a seat and a permission is a key. EVE's
 * Auditor role exists because corp theft is content and content requires a record.
 */
export type FamilyPermission =
  | 'vault.view'
  | 'vault.take'
  | 'upkeep.spend'
  | 'territory.declare'
  /**
   * ANSWERING a declaration by picking the window, which is a real power and a separate one from
   * declaring. It was `war.nominate` and DESIGN-DELTAS 1m.5 renames it: the nomination mechanic
   * it was named after was overturned at 1j.11, and a permission named after a deleted design is
   * how the deleted design gets reimplemented by somebody reading the schema.
   */
  | 'war.answer'
  | 'captain.payoff'
  | 'market.list_family'
  | 'roster.invite'
  | 'roster.remove'
  | 'commendation.award'

export const FAMILY_PERMISSIONS = [
  'vault.view',
  'vault.take',
  'upkeep.spend',
  'territory.declare',
  'war.answer',
  'captain.payoff',
  'market.list_family',
  'roster.invite',
  'roster.remove',
  'commendation.award',
] as const satisfies readonly FamilyPermission[]

export type MarketCategory =
  | 'weapons'
  | 'vehicles'
  | 'armor'
  | 'business'
  | 'contraband'
  | 'materials'
  | 'services'

/**
 * The seven, as DATA, for the same reason `FAMILY_ROLES` is: so the enum, the validator and the
 * thing that iterates them cannot drift apart.
 *
 * `server/src/routes/market.ts` carried the only copy of this list and it was the shape of the
 * market's Durable Object fan-out: seven `MarketDO` per Era, named `era{n}:market:{category}`.
 * Teardown has to enumerate those names for an Era the deploy is not running, so the list had to
 * leave the route it was constructing stubs in. `server/src/lib/roster.ts` says why enumerating
 * them beats rostering them.
 */
export const MARKET_CATEGORIES = [
  'weapons',
  'vehicles',
  'armor',
  'business',
  'contraband',
  'materials',
  'services',
] as const satisfies readonly MarketCategory[]

/**
 * The eight things the Market rail can be showing, which is the seven books plus `all`.
 *
 * `all` is an AISLE and never a shard: there is no `MarketDO` behind it and no listing carries it.
 * It exists because the rail's first tab says All and, until 3 August 2026, it silently loaded the
 * weapons book: a player who clicked it saw the weapons board under the word "All" and a catalogue
 * count of 135 over it, which is how "The Fence isn't working, it doesn't show anything" was
 * reported. `GET /api/market/all` reads all seven and merges.
 */
export type MarketAisle = MarketCategory | 'all'

/**
 * The SIX categories that have a catalogue behind them, which is not the same set as the seven
 * the Market screen models. `services` is a listing surface with no item defs: a player offers
 * a service, so there is nothing in `content/items/` to price, crush or repair.
 */
export type ItemCategory = 'weapons' | 'vehicles' | 'armor' | 'business' | 'contraband' | 'materials'

export const ITEM_CATEGORIES = [
  'weapons',
  'vehicles',
  'armor',
  'business',
  'contraband',
  'materials',
] as const satisfies readonly ItemCategory[]

/** SIX categories, because crimes.banner has six. `cyber` renders as "Wires" in every string. */
export type CrimeCategory = 'street' | 'smuggling' | 'vice' | 'fraud' | 'high_stakes' | 'cyber'

/** SIX types, because contracts.icons has six emblems. */
export type ContractType =
  | 'elimination'
  | 'disruption'
  | 'sabotage'
  | 'transport'
  | 'extortion'
  | 'protection'

export type PvpIntent = 'mug' | 'hospitalize' | 'message'

/** EIGHT outcomes, because pvp.results has eight artworks. */
export type PvpOutcomeKind =
  | 'clean_win'
  | 'narrow_win'
  | 'failed_attack'
  | 'target_escaped'
  | 'police_bust'
  | 'beef_escalated'
  | 'sitdown_broken'
  | 'sitdown_offered'

/** EIGHT outcomes, because crimes.result has eight artworks. */
export type CrimeOutcomeKind =
  | 'clean_success'
  | 'partial_success'
  | 'failure'
  | 'police_bust'
  | 'jailed'
  | 'hospitalised'
  | 'asset_seizure'
  | 'target_escaped'

/** SIX outcomes, because jobs.results has six artworks. */
export type JobOutcomeKind = 'success' | 'big_score' | 'partial' | 'failed' | 'busted' | 'escaped'

/** NINE states, because icons.beef has nine artworks. */
export type BeefState =
  | 'opened'
  | 'simmering'
  | 'escalated'
  | 'vendetta_sworn'
  | 'open_war'
  | 'sitdown_pending'
  | 'truce'
  | 'tribute_paid'
  | 'cooled_off'

/** TEN types, because warroom.icons has ten sabotage emblems. */
export type SabotageType =
  | 'bridge_block'
  | 'comms_blackout'
  | 'crane_jam'
  | 'fuel_line'
  | 'informant_tip'
  | 'lock_break'
  | 'payroll_burn'
  | 'power_cut'
  | 'road_spikes'
  | 'shipment_wreck'

/** FIVE resources, because warroom.stockpile_* has five artworks. */
export type StockResource = 'weapons' | 'ammo' | 'armor' | 'medical' | 'boosters'

/**
 * The phases a declared war passes through, and they are exactly what `warPhaseAt` returns.
 *
 * THIS USED TO CARRY `stalemate` AND `archived` AND IT WAS WRONG IN A WAY WORTH RECORDING.
 * `stalemate` is a SCORE OUTCOME, not an interval: GAME-DESIGN 8.6's table lists it beside the
 * four time phases because each of the five has a `warroom.window_*` plate, and a plate is not a
 * clock. A window that closes undecided is in `aftermath` like every other window, and what makes
 * it a stalemate is that the challenger did not clear `flipThreshold`. `archived` was internal to
 * a war object that is not constructed. `shared/war-window.ts` is the authority and this union is
 * now its `WarWindowPhase`, which `shared/test/war-window.test.ts` pins.
 */
export type WarPhase = 'declared' | 'mustering' | 'active' | 'aftermath' | 'closed'

/** TWELVE kinds, because icons.scanner has twelve artworks. */
export type ScannerKind =
  | 'antenna_tower'
  | 'dispatch_headset'
  | 'evidence_bag'
  | 'handcuffs'
  | 'informant_eye'
  | 'megaphone'
  | 'patrol_car'
  | 'radar_sweep'
  | 'radio_scanner'
  | 'roadblock_barrier'
  | 'siren_light'
  | 'wiretap_reel'

/**
 * FOUR methods, and the fourth is the way OUT of the third.
 *
 * `go_legit` sets `heat_reason` and schedules nothing, so before `back_to_street` there was no way
 * to stop running legit at all: the only exits were jail, hospital or a full lie-low cycle. That
 * was survivable while the 45 percent cut applied to Jobs alone. Phase 15 applied it to Crimes too,
 * correctly, and in doing so turned a lopsided bonus into a permanent 45 percent cut on every
 * payout in the game with no exit, which is a trap rather than a trade.
 *
 * `back_to_street` has its own plate, `heat.cooloff.back_to_street`, deliberately shot as the same
 * shop as `run_legit_business` from the same camera with the lamp off and the keys on the counter.
 * See DESIGN-DELTAS 1w.2 and 1w.14.
 */
export type CooloffMethod = 'bribe' | 'lie_low' | 'go_legit' | 'back_to_street'

/**
 * FOUR upgrade branches, and a Family may take exactly TWO of them for the Era.
 *
 * DATA-MODEL 3.2 and the Families mockup both carried six independent tracks
 * (`vault_reinforcement`, `ward_payroll`, `longshore_local`, `safehouse_network`, `armory`,
 * `front_companies`) with no exclusivity rule and effect lines that map to no mechanic in
 * GAME-DESIGN. GAME-DESIGN 8.2 names four branches, gives each a stated cap, and says a Family
 * takes two, "so branches are identity-defining". The exclusivity is the whole design: six
 * unconstrained tracks is a checklist, four with a pick-two is a decision.
 *
 * The SIX-level arity from DATA-MODEL is kept, because it is the only stated arity and it is
 * corroborated by 8.2's own budget line: two branches finished inside a twelve-week Era at six
 * levels each is one level a week, which is a cadence a twelve-person Family can actually hold.
 *
 * Recorded in docs/DESIGN-DELTAS.md 1i.2.
 */
export type UpgradeId = 'laundering' | 'discipline' | 'muscle' | 'connections'

export const UPGRADE_IDS = [
  'laundering',
  'discipline',
  'muscle',
  'connections',
] as const satisfies readonly UpgradeId[]

/** A Family takes two of the four. GAME-DESIGN 8.2. */
export const UPGRADE_BRANCHES_PER_FAMILY = 2

/** SIX kinds, because icons.warrant has six artworks. */
export type WarrantKind = 'bench' | 'search' | 'arrest' | 'wiretap' | 'seizure' | 'sealed_indictment'

/** Laundering infrastructure tier. 0 is the street fence. */
export type LaunderTier = 0 | 1 | 2 | 3

/**
 * An art id as it appears in art/manifest.json, resolved through game/src/art.ts `A()`.
 *
 * STAYS A BARE STRING, deliberately. `KnownAssetId` in game/src/art.ids.generated.ts is the union
 * of every id the resolver answers to, and it is what the client's own mock tables are annotated
 * with so a typo is a compile error. It cannot be this type. Values of `AssetId` come off the wire
 * out of content JSON that is hot-swappable in KV without a deploy, and the server validates them
 * with a regex rather than an enum (server/src/content/schema.ts), so neither side has compiled
 * against the set of ids that will actually be published. A union here would reject a legitimate
 * content push at the type level and prove nothing at runtime.
 *
 * The split is by ORIGIN, not by strictness: `KnownAssetId` for an id somebody WRITES,
 * `AssetId` for an id that ARRIVES. Every `KnownAssetId` is assignable to `AssetId`.
 */
export type AssetId = string

/**
 * Money is ALWAYS integer CENTS on the wire. Never a float, never a string, never dollars.
 *
 * BRANDED, because the type system had nothing to say when it was a bare `number` and a
 * hundredfold display error shipped and typechecked clean: the server sent 598258 meaning
 * $5,982.58 and the client rendered "$598,258". Both sides were `number`, so nothing anywhere
 * could tell them apart.
 *
 * The brand exists only at compile time. `Cents` IS a number at runtime, so `JSON.parse` still
 * produces usable values and arithmetic still works; what changes is that a `Dollars` can no
 * longer be handed to something expecting `Cents`.
 *
 * Arithmetic on branded numbers widens back to `number` (`a + b` where both are `Cents` is a
 * plain `number`), so the helpers in `shared/formulas.ts` are how a computed value gets its
 * brand back: `cents()`, `dollars()`, `addCents()`, `toDollars()`. Reach for those rather than
 * casting, because a cast is exactly the gesture that put the bug there in the first place.
 */
export type Cents = number & { readonly __brand: 'Cents' }

/**
 * Dollars, for the two places the game legitimately deals in them: a formula written in dollars
 * before `dollars()` converts it, and a screen still on mock data.
 */
export type Dollars = number & { readonly __brand: 'Dollars' }

/** Basis points. 5500 means 55.00 percent. Used wherever a rate crosses the wire. */
export type Bp = number

/** The largest integer that survives the JS layer intact. BigInt is unsupported in workerd. */
export const MAX_CENTS = 9_007_199_254_740_991

/**
 * Canon from game/src/game/state.ts and GAME-DESIGN.md section 3.1. These thresholds must not
 * move: the band is what other players see, the number is what you see.
 */
export const heatBand = (h: number): HeatBand =>
  h >= 90 ? 'rico' : h >= 75 ? 'critical' : h >= 50 ? 'dangerous' : h >= 25 ? 'watched' : 'calm'

export const HEAT_LABEL: Record<HeatBand, string> = {
  calm: 'Quiet',
  watched: 'Watched',
  dangerous: 'Dangerous',
  critical: 'Critical',
  rico: 'RICO Watch',
}

export const HEAT_NOTE: Record<HeatBand, string> = {
  calm: 'Nobody is looking at you.',
  watched: 'A patrol car took your plate.',
  dangerous: 'Risk of arrest high.',
  critical: 'They have your name on a board.',
  rico: 'Federal task force is building a case.',
}

/* -------------------------------------------------------------------- envelope */

export interface ApiOk<T> {
  ok: true
  data: T
  /** Present on every mutating response. The client replaces its whole player slice. */
  player: PlayerSnapshot | null
  /**
   * The next single-use action token, bound to the SAME intent as the request that produced it,
   * so a player can repeat an action without a round trip. Swap it in onSuccess. Never fetch
   * one separately: doing so turns a single-use nonce into a two-request dance an attacker can
   * interleave.
   */
  nextToken: string | null
  /**
   * Action tokens for the intents a screen can invoke, issued by the READ that renders it.
   * One token covers the whole set and is still single use, so spending it on `crime.commit`
   * spends it for `heat.cooloff` too and the response supplies the next one.
   *
   * The key is the screen's intent set joined by a comma, matching what the client must send
   * back in `X-Action-Token` alongside the intent it chose.
   */
  tokens: TokenBundle | null
  /** Authoritative clock. Never derive a countdown from Date.now(). */
  serverNowMs: number
  era: number
  manifestVer: number
}

/** A single-use token and the exact set of intents it is valid for. */
export interface TokenBundle {
  token: string
  intents: ActionIntent[]
  expiresAt: number
}

/**
 * How long a minted action token lives. Two minutes, and BOTH SIDES NEED THE NUMBER.
 *
 * The server needs it to set `exp` and to derive the dwell interval. The client needs it because
 * `nextToken` arrives with no expiry on the wire: it is a bare string on the envelope, and a
 * client that files it away without knowing when it dies will happily spend a token the server
 * has already timed out. It lived in `server/src/do/player.ts` alone until the client grew a
 * token registry that had to answer "is this one still good".
 */
export const ACTION_TOKEN_TTL_MS = 120_000


export interface ApiErr {
  ok: false
  error: {
    code: ApiErrorCode
    /** Player-facing, in voice. No em-dashes, no en-dashes. */
    message: string
    retryAfterMs: number | null
    field: string | null
  }
  serverNowMs: number
  era: number
  manifestVer: number
}

export type ApiEnvelope<T> = ApiOk<T> | ApiErr
export type ActionResult<T> = ApiEnvelope<T>

export type ApiErrorCode =
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'NEEDS_ELEVATION'
  | 'BAD_TOKEN'
  | 'REPLAY'
  | 'NOT_ENOUGH_ENERGY'
  | 'NOT_ENOUGH_NERVE'
  | 'NOT_ENOUGH_DIRTY'
  | 'NOT_ENOUGH_CLEAN'
  | 'IN_HOSPITAL'
  | 'IN_JAIL'
  | 'TARGET_SHIELDED'
  | 'TARGET_IN_HOSPITAL'
  | 'SITDOWN_ACTIVE'
  | 'RATE_LIMITED'
  | 'CAP_REACHED'
  | 'LOCKED'
  | 'EXPIRED'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'MAINTENANCE'
  | 'ERA_CLOSED'
  | 'CLIENT_TOO_OLD'
  | 'INTERNAL'

/** HTTP status for each error code. One table, so no handler invents its own mapping. */
export const ERROR_STATUS: Record<ApiErrorCode, number> = {
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  NEEDS_ELEVATION: 403,
  BAD_TOKEN: 409,
  REPLAY: 409,
  NOT_ENOUGH_ENERGY: 409,
  NOT_ENOUGH_NERVE: 409,
  NOT_ENOUGH_DIRTY: 409,
  NOT_ENOUGH_CLEAN: 409,
  IN_HOSPITAL: 409,
  IN_JAIL: 409,
  TARGET_SHIELDED: 409,
  TARGET_IN_HOSPITAL: 409,
  SITDOWN_ACTIVE: 409,
  RATE_LIMITED: 429,
  CAP_REACHED: 409,
  LOCKED: 423,
  EXPIRED: 410,
  NOT_FOUND: 404,
  CONFLICT: 409,
  MAINTENANCE: 503,
  ERA_CLOSED: 503,
  CLIENT_TOO_OLD: 426,
  INTERNAL: 500,
}

/* --------------------------------------------------------------------- anchors */

/**
 * A regenerating resource is NEVER sent as a number. It is sent as an anchor and derived on
 * read using the exact formula PlayerDO uses, so client and server cannot disagree by
 * construction. `value` is the settled amount at `asOfMs`, not the amount now.
 */
export interface Anchor {
  value: number
  max: number
  /** ms per tick. Energy 240_000, Nerve 720_000. */
  periodMs: number
  /** ms epoch of the last settled tick. NOT "now". */
  asOfMs: number
}

/**
 * Heat runs the same machinery in the opposite direction, with a floor of 0 instead of a cap.
 * The DECAY RATE is variable (state multipliers in GAME-DESIGN 3.6), so the period travels with
 * the value: any state change that alters the rate settles Heat first, then rewrites periodMs.
 */
export interface HeatAnchor {
  value: number
  /** ms per one point of decay. Base is 300_000 (1 per 5 minutes). 0 means decay is frozen. */
  periodMs: number
  asOfMs: number
  band: HeatBand
  /** Why decay is at this rate right now, for the UI to explain itself. */
  reason: HeatDecayReason
}

export type HeatDecayReason =
  | 'street'
  | 'go_legit'
  | 'lie_low'
  | 'jail'
  | 'hospital'
  | 'hitlist'
  | 'lay_low'

export const ENERGY_PERIOD_MS = 240_000
export const NERVE_PERIOD_MS = 720_000
export const HEAT_PERIOD_MS = 300_000

/* --------------------------------------------------------------------- player */

export interface PlayerSnapshot {
  playerId: string
  handle: string
  displayName: string
  portraitId: AssetId
  level: number
  xp: number
  xpNext: number
  rank: Rank
  nextRank: Rank | null
  /** PHASE 24. The Line this player drew at Soldier, or '' while none is. */
  line: Line
  energy: Anchor
  nerve: Anchor
  heat: HeatAnchor
  dirty: Cents
  clean: Cents
  /** Basis points, already Heat-penalised, ready to render. */
  launderRateBp: Bp
  launderTier: LaunderTier
  legend: number
  familyId: string | null
  familyName: string | null
  familyRole: FamilyRole | null
  crewId: string | null
  /*
   * `sponsorFavor` AND `reputation` WERE HERE AND ARE DELETED. DESIGN-DELTAS 1t.6.
   *
   * Both were published on every snapshot with no writer anywhere: every account in the game
   * reported reputation 50 and favour 0, forever, and nothing rendered either. Phase 13 builds a
   * record screen, which is exactly the surface that would have drawn them, and this project has
   * already caught the same failure twice: the Jobs rewards panel and the hardcoded "Top 1.2%".
   * A number a screen draws with no system behind it teaches a player the numbers are decorative.
   *
   * The COLUMNS stay in the Durable Object, unread. Dropping a column from a STRICT table is a
   * table rewrite, and a column nobody publishes cannot mislead anybody.
   *
   * `sponsorId` and `sponsorName` STAY, because the sponsorship system is real, has a writer and
   * is rendered on Home.
   */
  sponsorId: string | null
  sponsorName: string | null
  districtId: DistrictId
  hospitalUntil: number
  jailUntil: number
  shieldUntil: number
  cooloffUntil: number
  /** 0 to 100 while in RICO Watch. See GAME-DESIGN 3.8. */
  indictmentProgress: number
  warrant: WarrantKind | null
  refills: { energy: number; nerve: number; resetAt: number }
  /**
   * The brake, and it is NOT hidden. This field's comment used to say "never surfaced in the UI",
   * which was true and was the wrong design.
   *
   * GAME-DESIGN 13.3 constraint 3 is the reason it changed: "the `rng_seed` for every roll is
   * still stored and every outcome is still reproducible from the ledger. If a player appeals, the
   * replay tool shows exactly what happened, including the tier modifier. The game does not lie
   * about its own dice." A multiplier that silently shaves a quarter off a payout, on a screen
   * that shows the payout, is the Call of Duty failure 13.3 spends a paragraph on. The Reveal
   * shows it. DESIGN-DELTAS 1r.12.
   */
  shadowTier: 0 | 1 | 2 | 3
  manifestVer: number
}

/**
 * THE LINEUP. GAME-DESIGN 13.2's in-fiction challenge, on the wire.
 *
 * Six portrait ids and one authored sentence describing exactly one of them. The ANSWER is not
 * here and never will be: there is no client-side secret in a single-page application, so
 * whatever the client could grade a script driving the same client could grade. The grading is on
 * the server and this is what the browser is told.
 *
 * `issuedAt` is the server's instant, and the interval between it and the answer arriving is what
 * the challenge is actually FOR. 13.2: a bot "either fails it or has to build a solver, and
 * building a solver is an expensive and separately detectable step up in commitment."
 */
export interface Lineup {
  id: string
  /** Six `AssetId` portraits, in display order. */
  suspects: string[]
  /** The witness statement. One line of authored description, out of CONTENT-BIBLE 3.1/3.3/3.4. */
  subject: string
  issuedAt: number
}

export interface PublicProfile {
  playerId: string
  handle: string
  displayName: string
  portraitId: AssetId
  level: number
  rank: Rank
  legend: number
  familyId: string | null
  familyName: string | null
  familyRole: FamilyRole | null
  crestId: AssetId | null
  districtId: DistrictId
  heat: number
  heatBand: HeatBand
  status: PlayerStatus
  hospitalUntil: number
  jailUntil: number
  shieldUntil: number
  netWorth: Cents
  titles: string[]
  frameId: AssetId | null
  beefWithViewer: BeefRow | null
  bountyTotal: Cents
  eras: number
}

export type PlayerStatus = 'online' | 'away' | 'offline' | 'hospital' | 'jailed'

/* ---------------------------------------------------------------- world state */

/**
 * The five states of an Era, from `era.state` in the permanent database.
 *
 * `planned` and `seeding` are the fortnight before it opens; `live` is the twelve weeks;
 * `closing` is the ceremony; `archived` is everything after, and it is the only state in which
 * an Era's Durable Objects may be torn down.
 */
export type EraState = 'planned' | 'seeding' | 'live' | 'closing' | 'archived'

export interface EraMeta {
  n: number
  name: string
  plateId: AssetId
  startsAt: number
  endsAt: number
  week: number
  of: number
  /** SHA-256(eraSecret). The secret is revealed at Era close so every roll becomes verifiable. */
  secretHash: string
  /** Straight off the `era` row. `closing` is what the Era-over screen renders. */
  state: EraState
  /**
   * `ERA_SECRET` itself, and null for the whole Era.
   *
   * It becomes a string at The Reveal, after the write path is dead, and it stays one forever.
   * Publishing it while anybody can still act would hand every remaining player their own next
   * outcomes: the roll for action `n` is a pure function of `(secret, player, n)`.
   */
  secret: string | null
  /**
   * The Sweep. Weeks 11 and 12, published as fixed and identical in every Era, and no Era
   * variant may touch it. DESIGN-DELTAS 1j.14.
   */
  sweep: boolean
}

export interface BeatState {
  district: DistrictId
  /** The district that was Hot in the previous hour. Pays 1.15x, 1.30x Heat. */
  cooling: DistrictId | null
  /** 0.40 means +40% in the hot district. */
  payout: number
  heatMult: number
  startsAt: number
  endsAt: number
  index: number
}

export type BeatRelation = 'hot' | 'cooling' | 'cold'

export interface DistrictState {
  id: DistrictId
  name: string
  short: string
  control: 'you' | 'allied' | 'contested' | 'enemy' | 'neutral'
  poly: [number, number][]
  at: [number, number]
  blocks: number
  income: Cents
  heat: number
  tone: string
  blurb: string
  heroId: AssetId
  insetId: AssetId
  /**
   * The two landmark blocks this district is known by, authored in CONTENT-BIBLE section 2.
   *
   * On the wire because 24 of the ruled 40 blocks have an authored name and nothing could reach
   * them: the extractor dropped the names and kept the ids, so `Territory.tsx` filled the gap
   * with forty invented replacements including one built on a surname the bible bans by name.
   * The other 16 are an authoring ask recorded in `docs/CONTENT-GAPS.md`, and the screen says
   * how many are named rather than inventing the difference.
   */
  landmarks: { id: AssetId; name: string }[]
}

export interface FeedItem {
  id: string
  kind: 'news' | 'global'
  text: string
  at: number
}

export interface GlobalFlags {
  maintenance: boolean
  message: string
  minClientBuild: number
  warWindowFreeze: boolean
}

export interface BootPayload {
  player: PlayerSnapshot
  era: EraMeta
  beat: BeatState
  /** 2016 entries, one per hour of the Era. Fetched once, Beat derived locally thereafter. */
  beatSchedule: DistrictId[]
  districts: DistrictState[]
  flags: GlobalFlags
  manifestVer: number
  wsBase: string
  serverNowMs: number
  /**
   * A standing Lineup, or null, which it is for almost everybody almost always.
   *
   * On BOOT because the loop does not close otherwise, and looking at the screen in a browser is
   * what found that: a Lineup has to be ISSUED before it can REFUSE anything, and the client
   * fetched it only on a refusal, so nothing ever issued one and the interstitial was unreachable
   * from the real client. Boot issues it; the refusal path in `act()` catches the mid-session case
   * where one was issued in another tab or on a read the player never looked at.
   */
  lineup: Lineup | null
  /**
   * `unverified` until the emailed link is followed. PLAY IS NOT GATED ON IT and never will be.
   *
   * On boot because until Phase 13 nothing in the client knew this field existed, so every account
   * in the game was permanently unverified and nothing anywhere said so. The link was mailed, the
   * route that consumes it worked, and there was no client path that could reach it. The shell now
   * carries one line, once, with a way to send the letter again.
   */
  accountStatus: 'unverified' | 'active'
}

/* ------------------------------------------------------------------------ identity */

export interface SignupBody {
  handle: string
  email: string
  password: string
  displayName?: string
  /** Turnstile response token from the widget on the landing page. */
  turnstile: string
  /** Accepted terms version. Stored so a change can be re-prompted. */
  tosVersion?: number
}

export interface LoginBody {
  email: string
  password: string
  /** Present only when the account has been flagged for a challenge. */
  turnstile?: string
}

export interface AuthResult {
  playerId: string
  handle: string
  displayName: string
  /** `unverified` until the emailed link is followed. Play is not gated on it. */
  status: 'unverified' | 'active'
  /** Era-scoped: a brand new PlayerDO was created by this request. */
  created: boolean
  expiresAt: number
}

export interface ElevateResult {
  elevatedUntil: number
}

/**
 * `GET /api/auth/gate`. The only thing a SIGNED-OUT browser is told, and it is one string.
 *
 * A Turnstile sitekey is public by construction: it is printed in the markup of every site that
 * runs one. It is nonetheless served rather than baked into the bundle, because one build is
 * deployed to development, staging and production and each of those is a different widget with a
 * different allowed hostname. A build-time constant would mean three builds, and the wrong one of
 * the three would fail with `hostname-mismatch`, which is the least legible error in the stack.
 *
 * `tosVersion` rides along because the signup body carries it and a client that invents the number
 * is a client that records consent to a document nobody chose.
 */
export interface GatePayload {
  turnstileSiteKey: string
  tosVersion: number
  /**
   * TRUE WHEN THE CHALLENGE IS NOT REALLY RUNNING, and the Gate prints a line saying so.
   *
   * Cloudflare's testing pair short-circuits `verifyTurnstile` before the network call, so the
   * siteverify request, the `action` assertion and the `hostname` assertion are all unexercised.
   * The widget renders, a token is minted and the round trip is real; the verdict is a constant.
   *
   * It is published rather than inferred because the failure Phase 13 opened with was invisibility,
   * not absence: the door looked like it worked in every environment anybody had deployed. A
   * screenshot of a staging Gate now says which one it is, on the screen, in words.
   */
  testing: boolean
}

/**
 * THE WIDGET'S DECLARED ACTION, asserted by the server against the siteverify response.
 *
 * One string for both forms, and that is the owner's instruction rather than this project's
 * preference: the Turnstile integration is attributed by action, and every `cf-turnstile` widget
 * carries this one. It is shared so the client that renders it and the server that asserts it
 * cannot drift, which is the property that makes asserting it worth anything at all.
 *
 * **WHAT THAT COSTS, stated rather than buried.** `server/src/lib/turnstile.ts` asserts `action`
 * so a token minted by one widget cannot be spent at another endpoint. With one action across both
 * forms, that assertion still refuses a token from any OTHER widget or site, which is the case it
 * was written for, and no longer separates signup from password reset. The residual exposure is
 * small: a token is single use, both endpoints are separately rate limited per address and per
 * network, and `/auth/reset/request` answers identically whether or not the address exists. It is
 * recorded at DESIGN-DELTAS 1t.1 rather than left for somebody to find in the assertion.
 */
export const TURNSTILE_ACTION = 'turnstile-spin-v2'

/**
 * The address a player is told to write to, and the only one this game claims exists.
 *
 * It lives here rather than inline at the two call sites because a support address that appears in
 * copy is a PROMISE, and the failure mode is silent: `/auth/signup` refuses a datacenter connection
 * with "write to support and we will sort it", which is a documented appeal path, and for the whole
 * life of this project there was no mailbox behind it. A blocked player writing to the address the
 * refusal named would have got a bounce, and nobody would ever have known.
 *
 * `ashport.city` now carries Cloudflare Email Routing at the apex, forwarding this address to a
 * verified destination, and it is proven by delivery rather than by a status field. The Resend
 * bounce MX sits on `send.ashport.city`, so the two do not contend for the apex.
 *
 * Anything that offers a player a way to reach a human must use THIS constant, so that the promise
 * and the mailbox can never drift apart.
 */
export const SUPPORT_EMAIL = 'admin@ashport.city'

/** `POST /api/auth/verify` and `POST /api/auth/verify/resend`. */
export interface VerifyResult {
  verified: boolean
}

export interface ResendResult {
  /** Always true. It never says whether an address was already verified. */
  sent: boolean
  /** Authored, in voice. What the player reads. */
  message: string
}

/** `POST /api/auth/reset/request`. Identical whether or not the address exists. */
export interface ResetRequestResult {
  sent: boolean
}

/** `POST /api/auth/reset/confirm`. */
export interface ResetConfirmResult {
  reset: boolean
  sessionsRevoked: number
}

/** GET /api/health. Deliberately public and deliberately boring. */
export interface HealthPayload {
  ok: true
  service: 'ashport'
  buildEnv: string
  commit: string
  era: number
  eraSlot: string
  manifestVer: number
  compatibilityDate: string
  serverNowMs: number
  /** Every binding the Worker expects, and whether it actually resolved. Trap #1 of staging. */
  bindings: Record<string, boolean>
}

/* ------------------------------------------------------------------- core loop */

/**
 * A crime's tuning, as authored in `content/crimes.json` and published to KV.
 *
 * The definition is resolved by the WORKER and passed into `PlayerDO`, never read from KV
 * inside the object. Two reasons, and both are load-bearing: a KV read inside a Durable Object
 * would be an `await` in the middle of a write run, which ends the implicit transaction and
 * opens the interleave window; and the client never supplies it, so the tuning stays
 * server-authoritative while remaining hot-swappable without a deploy.
 */
export interface CrimeDef {
  id: string
  name: string
  cat: CrimeCategory
  risk: Risk
  /** Level gate. Below this the crime is visible and locked, never hidden. */
  levelGate: number
  energy: number
  nerve: number
  /** Pays 1.0x here and 0.9x elsewhere. */
  homeDistrict: DistrictId
  districts: DistrictId[]
  thumbId: AssetId
  /**
   * Heat on a clean run before the Beat multiplier and variance, AUTHORED PER CRIME.
   *
   * It currently equals the risk tier's base for all 26, but it lives in the content bundle
   * rather than in `RISK_HEAT` so a single crime can be re-tuned by publishing a bundle instead
   * of shipping a deploy.
   */
  baseHeat: number
  crew: string
  window: string
  pitch: string
}

export interface CrimeOffer {
  id: string
  name: string
  cat: CrimeCategory
  thumbId: AssetId
  featuredId: AssetId | null
  /** Base payout before Beat and variance, in cents. */
  payout: Cents
  heat: number
  /** Basis points, already resolved against THIS player's Heat, district and tempo. */
  bustBp: Bp
  energy: number
  nerve: number
  districtId: DistrictId
  crew: string
  window: string
  pitch: string
  lockedReason: string | null
}

/**
 * What `GET /api/crimes` returns.
 *
 * The offers arrive already priced against this player at this instant, including the bust
 * threshold in basis points. The client renders that number and never derives it, but the same
 * `shared/formulas.ts` is available to it, so a mismatch between the shown number and the rolled
 * one is a test failure rather than a rumour.
 */
export interface CrimesPayload {
  offers: CrimeOffer[]
  beat: BeatState
  /** Where the player is standing. Offers are ordered with this district's crimes first. */
  districtId: DistrictId
  /**
   * What to do now. Never empty, and never pointing at a locked tile: if the whole board is
   * gated, this carries a Jobs answer instead. A new character standing in Dockside sees every
   * crime locked behind a level gate, and the Planner has to have an answer for that player.
   */
  advice: PlannerAdvice[]
}

export interface JobsPayload {
  chains: JobChainState[]
  beat: BeatState
}

/**
 * One thing the player could do right now, and why.
 *
 * The Planner is the most important onboarding surface in the game: the thing that kills new
 * players in this genre is opening a page with thirty numbers and no idea which one to click.
 * It is computed on the SERVER because the server is the only thing that knows the whole board,
 * and it must NEVER point at something the player cannot act on.
 */
export interface PlannerAdvice {
  kind: 'crime' | 'job' | 'cool_off' | 'wait' | 'travel'
  /** Which screen the action lives on, so the card can be a link rather than a riddle. */
  screen: 'home' | 'jobs' | 'crimes' | 'ward'
  /** The id of the crime, chain or district being pointed at. */
  refId: string | null
  label: string
  title: string
  /** One or two sentences, in voice, saying why this and not the other thing. */
  body: string
  /** 'safe' and 'greedy' are the two ends of the board. 'only' means there is one answer. */
  tone: 'safe' | 'greedy' | 'only'
}

export interface CrimeOutcome {
  kind: CrimeOutcomeKind
  artId: AssetId
  headline: string
  body: string
  dirtyGained: Cents
  dirtyLost: Cents
  xpGained: number
  heatDelta: number
  jailUntil: number | null
  hospitalUntil: number | null
  /**
   * The items the Sealed Indictment took, if it fired on this action. Null otherwise.
   *
   * This field has been on the wire since Phase 2 and was the literal `null` at every site. One
   * crime can both fail its bust roll AND carry Indictment Progress to 100, which is the single
   * action in the game that produces a bust sentence and a seizure together.
   */
  seized: { itemId: string; qty: number }[] | null
  /** The whole event, when one fired. `seized` is its item list and this is the rest of it. */
  indicted: IndictmentEvent | null
  drops: { itemId: string; qty: number }[] | null
  beat: BeatRelation
  /** The threshold the roll was compared against, in basis points. Published, always. */
  bustBp: Bp
  /** Ties this outcome to a stored nonce for support and for post-Era verification. */
  rollId: string
  /** PHASE 20. The level before and after, and the cap a level-up raised Energy to. */
  levelBefore: number
  levelAfter: number
  energyMaxAfter: number
}

/** The five difficulty bands. Two beats each, by beat index: `jobDifficulty()` in formulas. */
export type JobDifficulty = 'Easy' | 'Medium' | 'Hard' | 'Very Hard' | 'Extreme'

/**
 * PHASE 22. What a beat drops, at the odds it is rolled at for THIS player right now.
 *
 * `chance` is the number the server will roll against on the next run, which is the mastery
 * tier the beat is at; it is on the wire rather than derived on the client so the row prints
 * the same number the write path reads. The pool is named so the row can say what falls out.
 */
export interface JobDrops {
  /** 0 to 1, at the player's current mastery on this beat. */
  chance: number
  /** The tier the beat drops at, which is its difficulty band's. */
  tier: number
  /** Every material in the pool, by name. */
  materials: string[]
  /** How many pieces of gear share the pool, one drop in four. 0 when none is under the cap. */
  gear: number
}

/** PHASE 22. What a beat needs before it will run. The row states it; the server enforces it. */
export interface JobRequirement {
  itemId: string
  name: string
  qty: number
  /** True when the beat's FIRST run spends it; false when it only has to be carried. */
  consumed: boolean
  /** How many the player holds right now, across every stack. */
  have: number
  /** True once a consumed requirement has been paid, which is once the beat has run at all. */
  spent: boolean
  /** The beat in this chain that drops it, by name, or null when none before it does. */
  droppedBy: string | null
}

export interface JobStep {
  id: string
  name: string
  desc: string
  iconId: AssetId
  artId: AssetId
  payout: Cents
  energy: number
  heat: 0
  diff: JobDifficulty
  step: number
  state: 'locked' | 'open' | 'active' | 'done'
  /**
   * WHY it is locked, in voice, or null when it is not. "Locked, never hidden" only works if
   * the lock is legible: a greyed row with a padlock tells a player they are unwelcome, and a
   * row that says "Three more runs on The Ground Floor" tells them what to do next.
   */
  lockedReason: string | null
  runs: number
  mastery: MasteryTier
  masteryNext: number | null
  /** PHASE 22. What this beat drops and at what odds, for this player, now. */
  drops: JobDrops
  /** PHASE 22. What this beat needs before it runs, or null when it needs nothing. */
  requires: JobRequirement | null
}

export type MasteryTier = 'none' | 'bronze' | 'silver' | 'gold'

export interface JobChainState {
  id: DistrictId
  label: string
  title: string
  pitch: [string, string]
  heroId: AssetId
  len: number
  time: string
  diff: JobStep['diff']
  focus: string
  /**
   * This chain's district against the current rotation. Each chain is worked where it is set,
   * so at any instant exactly one chain is Hot and at most one is Cooling.
   */
  beat: BeatRelation
  /** Crime Districts ship no chain in Era 1. The screen renders all 12 and marks these. */
  comingSoon: boolean
  /**
   * One authored sentence naming who stands in the way of this chain, from
   * `content/jobs/<district>.json`. On the wire because nothing rendered it and the screen had
   * filled the gap with twelve invented quotes attributed to a canon character by name and face.
   */
  antagonist: string
  objective: {
    title: string
    body: string
    checks: { t: string; have: number; need: number }[]
    done: boolean
  }
  jobs: JobStep[]
}

export interface JobOutcome {
  kind: JobOutcomeKind
  artId: AssetId
  headline: string
  body: string
  /** The whole batch's take, summed. One ledger row per run underneath it. */
  dirtyGained: Cents
  xpGained: number
  heatDelta: 0
  stepAdvancedTo: number | null
  masteryBefore: MasteryTier
  masteryAfter: MasteryTier
  chainComplete: boolean
  beat: BeatRelation
  /** The first run's roll. Every run in the batch has its own `crime_log` row. */
  rollId: string
  /**
   * PHASE 20. How many runs this one tap was (1 to `JOB_RUN_MAX`), the step's run count after
   * them, and the level before and after so the plate can say "Level 6" without deriving it
   * from the snapshot. `energyMaxAfter` is what a level-up raised the cap to.
   */
  times: number
  runs: number
  levelBefore: number
  levelAfter: number
  energyMaxAfter: number
  /**
   * PHASE 22. What the batch dropped, summed per item, or null when nothing did. Ids only, as
   * `CrimeOutcome.drops`: the client names them off the catalogue it already holds.
   */
  drops: { itemId: string; qty: number }[] | null
  /** PHASE 22. The requirement this batch spent on the beat's first run, or null. */
  consumed: { itemId: string; qty: number } | null
}

export interface LaunderOutcome {
  dirtyIn: Cents
  cleanOut: Cents
  rateBp: Bp
  haircut: Cents
  heatPenaltyBp: Bp
}

/* ---------------------------------------------------------------------- money */

/**
 * A laundering tier as the Money screen offers it.
 *
 * `rateBpAtCurrentHeat` is what this tier would pay THIS player RIGHT NOW, not the headline
 * rate, because the headline rate is the one number in the ladder a player cannot act on: it
 * only exists at Heat 50 or below and the players who launder most are never there.
 */
export interface TierOffer {
  tier: LaunderTier
  name: string
  cost: Cents
  bonusBp: Bp
  heatFloor: number
  rateBpAtCurrentHeat: Bp
  owned: boolean
  affordable: boolean
  lockedReason: string | null
}

/**
 * What `GET /api/money` returns: everything the laundering and fence surfaces need, priced
 * against this player at this instant.
 *
 * `rateBp` here is THE number the commit will use. `POST /api/money/launder` takes it back as
 * `expectRateBp` and REFUSES if it has moved, so "the number the client rendered is the number
 * that executed" is a server-enforced guarantee rather than a convention. Heat decays, so the
 * rate genuinely does move; refusing is how the promise survives that.
 */
export interface MoneyPayload {
  dirty: Cents
  clean: Cents
  heat: number
  launderTier: LaunderTier
  /** Basis points, already Heat-penalised. The commit rolls against exactly this. */
  rateBp: Bp
  /** How many basis points of the rate the current Heat is costing. 0 at or below Heat 50. */
  heatPenaltyBp: Bp
  /** The smallest batch the fence will take. */
  minBatch: Cents
  /** What the whole dirty balance would clear at, right now. */
  previewCleanOut: Cents
  tiers: TierOffer[]
  /** The seller-side fee curve, in basis points, for THIS player's Heat. */
  fenceFeeBp: Bp
  fence: FenceOffer[]
}

/** One stack of contraband the NPC fence will take, priced against this player's Heat. */
export interface FenceOffer {
  slotId: string
  itemId: string
  name: string
  artId: AssetId
  rarity: Rarity
  qty: number
  /** Catalogue value of one unit, before the fee. */
  unitBase: Cents
  /** Dirty cash for the whole stack after the fee, at the published `feeBp`. */
  netAll: Cents
  feeBp: Bp
}

export interface FenceOutcome {
  slotId: string
  itemId: string
  name: string
  qty: number
  gross: Cents
  fee: Cents
  net: Cents
  feeBp: Bp
}

export interface TierOutcome {
  tier: LaunderTier
  cost: Cents
  rateBp: Bp
  heatFloor: number
}

/**
 * One movement of money, as the player's own ledger records it.
 *
 * Append-only and complete: for each currency the deltas sum to the balance, and there is no
 * path anywhere in the game that moves money without writing one of these. That is the promise
 * the whole economy rests on, and it is a promise a player can check for themselves rather than
 * one they have to take on trust, which is why this is a player-facing endpoint and not an
 * admin one.
 */
export interface LedgerEntry {
  id: string
  currency: Currency
  delta: Cents
  /** The balance in that currency immediately after this row. */
  balance: Cents
  reason: LedgerReason
  refId: string | null
  /** Which content version priced it. A payout that looks wrong in week six is traceable. */
  manifestVer: number
  at: number
}

export interface LedgerPage {
  rows: LedgerEntry[]
  /**
   * Pass BOTH back for the next page, or null at the end of the history.
   *
   * Two parts, and both are required. Laundering writes a dirty row and a clean row in the same
   * millisecond by construction, so a timestamp is not a unique position: a cursor that carried
   * only `at` skipped every remaining row at that instant when a page boundary fell inside it,
   * and a client adding up its pages reached a different total than the server did. The ids are
   * ULIDs and therefore lexicographically ordered, which makes `(at, id)` a total order.
   */
  cursor: number | null
  cursorId: string | null
  /** Running totals over the WHOLE history, not just this page. */
  totals: { dirty: Cents; clean: Cents }
  /** The live balances. These equal `totals` unless something moved money without a row. */
  balance: { dirty: Cents; clean: Cents }
  /** Cash held against a live market order. Not spent, and not spendable twice. */
  escrow: { dirty: Cents; clean: Cents }
}

export interface CooloffOutcome {
  method: CooloffMethod
  artId: AssetId
  heatBefore: number
  heatAfter: number
  cost: Cents | null
  untilMs: number | null
}

/* ---------------------------------------------------------------------- market */

export interface Listing {
  id: string
  /**
   * The catalogue id. Without it a board row cannot be turned into a buy request and cannot be
   * joined to the catalogue for its stat line, which is the whole comparison a buyer makes.
   */
  itemId: string
  mkt: 'player' | 'black'
  cat: MarketCategory
  rarity: Rarity
  kind: string
  name: string
  sub: string
  artId: AssetId
  rarityPlateId: AssetId
  sellerId: string
  seller: string
  verified: boolean
  price: Cents
  cur: Currency
  qty: number
  cond: number | null
  income: Cents | null
  occupancy: number | null
  /** Exactly 14 points. */
  trend: number[]
  listedAt: number
  expiresAt: number
}

export interface BookLevel {
  price: Cents
  qty: number
  listings: number
}

export interface Fill {
  id: string
  listingId: string
  itemId: string
  qty: number
  unitPrice: Cents
  currency: Currency
  fee: Cents
  at: number
}

export interface PriceStat {
  itemId: string
  name: string
  last: Cents
  vwap24h: Cents
  low24h: Cents
  high24h: Cents
  vol24h: number
  deltaBp: Bp
  trend: number[]
}

export interface InventoryItem {
  slotId: string
  itemId: string
  category: MarketCategory
  rarity: Rarity
  name: string
  /** The authored one-liner from `content/items/*.json`, so a row reads as a thing. */
  sub: string
  artId: AssetId
  rarityPlateId: AssetId
  tier: number
  /** Catalogue value of ONE unit. Drives repair, the Crusher and the Fence price floor. */
  baseValue: Cents
  qty: number
  cond: number | null
  equipped: boolean
  income: Cents | null
  occupancy: number | null
  /** Escrowed against a live listing. Cannot be equipped, fenced, gifted or listed again. */
  lockedUntil: number
}

/* --------------------------------------------------------------------- catalogue */

/**
 * The 135 authored items, as the client sees them.
 *
 * Fetched ONCE per manifest version and cached for that version's lifetime, exactly like the
 * Beat schedule: it is immutable for a version by construction, so re-sending it on every
 * market page load would be pure waste. It is NOT in the boot payload because it is roughly
 * ten times the size of everything else there and most sessions never open the Market.
 *
 * Discriminated on category because the six stat lines are six different things. Three are
 * capability (a triple, 1 to 100) and three are money the bible authored outright.
 */
interface CatalogCommon {
  id: string
  name: string
  artId: AssetId
  rarityPlateId: AssetId
  tier: number
  rarity: Rarity
  /** The authored one-liner. */
  line: string
  /** Catalogue value of one unit, in cents. Authored where the bible priced it, else the curve. */
  baseValue: Cents
  /** What the Crusher pays for it: 8 percent of base, guaranteed, no roll. */
  crusher: Cents
  /** The Fence's price floor: 2x Crusher value. Era-late dumping cannot zero the economy. */
  priceFloor: Cents
}

export interface CatalogGear extends CatalogCommon {
  category: 'weapons' | 'armor' | 'vehicles'
  /** `absolute` is the item's own line. `modifier` is an accessory's signed contribution. */
  statMode: 'absolute' | 'modifier'
  /** weapons Dmg/Acc/Con, armor Prot/Mob/Con, vehicles Spd/Cap/Not. */
  stats: [number, number, number]
  /** The first stat, which is the one `power()` reads. Named so the UI need not know the order. */
  power: number
  /** What repairing it from 0 to 100 Condition costs, in clean cents. */
  fullRepair: Cents
}

export interface CatalogBusiness extends CatalogCommon {
  category: 'business'
  /** Gross clean cents per day. */
  incomeDaily: Cents
  /** Clean cents per day. */
  upkeepDaily: Cents
  /** Income minus upkeep. What a front is actually worth per day. */
  netDaily: Cents
  /** Authored, and NOT a second laundering system. See docs/DESIGN-DELTAS.md. */
  launderPct: number
}

export interface CatalogContraband extends CatalogCommon {
  category: 'contraband'
  slots: number
  /** What it adds to a bust: a case of scotch is a fine, printing plates are a federal count. */
  legalWeight: number
}

export interface CatalogMaterial extends CatalogCommon {
  category: 'materials'
  slots: number
  feeds: string
}

export type CatalogItem = CatalogGear | CatalogBusiness | CatalogContraband | CatalogMaterial

export interface CatalogPayload {
  manifestVer: number
  items: CatalogItem[]
}

/**
 * A catalogue row narrowed to what putting an item in a pocket needs.
 *
 * Resolved by the WORKER from KV and passed into `PlayerDO`, for the same reason `CrimeDef` is:
 * a KV read inside a Durable Object is an `await` in the middle of a write run. The client never
 * supplies one.
 */
export interface ItemGrant {
  itemId: string
  name: string
  category: MarketCategory
  rarity: Rarity
  tier: number
  artId: AssetId
  rarityPlateId: AssetId
  sub: string
  /** Catalogue value of ONE unit, in cents. Authored if the bible has one, else the curve. */
  baseValue: number
  qty: number
  /**
   * NET clean cents per day, for a front business and nothing else.
   *
   * It rides on the grant rather than being looked up later because a content republish must
   * never re-price a building somebody already owns. The catalogue is the shop window; the
   * inventory row is the deed.
   */
  income?: number
}

/* ------------------------------------------------------------------------- gear */

/** The three equipment slots. One item each, and they are exactly the three gear categories. */
export type GearSlot = 'weapons' | 'armor' | 'vehicles'
export const GEAR_SLOTS = ['weapons', 'armor', 'vehicles'] as const satisfies readonly GearSlot[]

export interface EquipOutcome {
  slot: GearSlot
  equipped: InventoryItem | null
  unequipped: InventoryItem | null
  power: number
}

export interface RepairOutcome {
  slotId: string
  name: string
  from: number
  to: number
  cost: Cents
}

export interface CrusherOutcome {
  slotId: string
  name: string
  qty: number
  /** What came out: always a row from `content/items/materials.json`, never invented. */
  materialId: string
  materialName: string
  materialQty: number
  value: Cents
}

export interface BusinessCollectOutcome {
  collected: Cents
  hours: number
  /** True when the 60 hour accrual ceiling had already been reached and time was lost. */
  capped: boolean
}

/**
 * ONE FRONT, ON ITS OWN. Phase 21.
 *
 * `GET /api/gear` published the till as one aggregate: a count, a total, the longest hours and
 * a capped flag. A player with three fronts could not tell which one had stopped earning. Each
 * front's own till, hours and net per day, so the Fronts panel can show a row per building.
 * Collecting is still one verb over all of them, because collecting early destroys nothing.
 */
export interface FrontRow {
  slotId: string
  itemId: string
  name: string
  sub: string
  artId: AssetId
  qty: number
  /** Net clean per day for this front, times its quantity. */
  netDaily: Cents
  /** What is in this front's till right now. */
  pending: Cents
  /** Hours banked, capped at the accrual ceiling. */
  hours: number
  /** True once this front has stopped earning. */
  capped: boolean
  acquiredAt: number
  /** PHASE 22. 1 to `FRONT_LEVEL_CAP`. `netDaily` already carries the level's multiplier. */
  level: number
  /** PHASE 22. What the next level costs, or null at the cap. */
  upgrade: FrontUpgradeOffer | null
}

/**
 * PHASE 22. The price of a front's next level, computed by the server against the row: clean
 * cash off the front's own net per day, and materials of the front's own tier.
 */
export interface FrontUpgradeOffer {
  toLevel: number
  cost: Cents
  materialId: string
  materialName: string
  materialQty: number
  /** How many of that material the player holds right now. */
  materialHave: number
  /** Net per day once the level is bought, for the row to print the difference. */
  netDailyAfter: Cents
}

export interface FrontUpgradeOutcome {
  slotId: string
  name: string
  level: number
  cost: Cents
  materialId: string
  materialName: string
  materialQty: number
  /** What the till held at the old rate, paid out before the rate changed. */
  collected: Cents
  netDaily: Cents
}

/* ------------------------------------------------------------------ push, Phase 23 */

/** The three things that arrive on their own, and the test ping the settings card sends itself. */
export const PUSH_KINDS = ['energy', 'beat', 'mugged', 'ping'] as const
export type PushKind = (typeof PUSH_KINDS)[number]

/** One browser's subscription, as the card sees it. The endpoint itself never rides here. */
export interface PushSubscriptionWire {
  id: string
  /** The push service's origin, which is all the card needs to say "via Google" or "via Apple". */
  service: string
  kinds: PushKind[]
  ua: string
  createdAt: number
  /** True for the browser that is asking, matched on the endpoint it sent. */
  mine: boolean
}

export interface PushPayload {
  /** The VAPID public key, base64url, or '' when this deployment has no keys. */
  publicKey: string
  subscriptions: PushSubscriptionWire[]
  /** The last few pushes this account was sent, newest first. */
  recent: { kind: PushKind; title: string; state: string; at: number }[]
}

/** What a refill is worth, and whether this player has one to spend. */
export interface RefillOffer {
  kind: 'energy' | 'nerve'
  available: number
  /** Why there is none, in voice, or null when there is one. */
  lockedReason: string | null
  /** ms epoch when the rolling 24 hour window next frees a slot, or 0 when one is free now. */
  readyAt: number
  /** Every consumed refill adds this much Heat. Published, because it is the whole decision. */
  heatCost: number
}

/**
 * THE MORNING LINE. `docs/PHASE-6B.md` section 6.
 *
 * The rack renders EVERY slot always, including the ones you have not earned today, showing the
 * authored `lockedReason` already on the wire. The read is the teaching: GAME-DESIGN 2.3's three
 * earned sources are only legible if you can see the two you did not earn.
 */
export interface MorningLine {
  /** 0 to 7, Era-scoped, decayed to now. */
  card: number
  cardMax: number
  /** True when today's claim has not been taken. Claiming is unconditional and instant. */
  claimable: boolean
  /**
   * The rule, in the exact words. It is the specific inverse of the reset-to-day-1 streak the
   * design literature identifies as the predatory core of streak design, and it is the most
   * important string in the feature.
   */
  rule: string
  /** What a refill costs in Heat at this Card position. The ONLY thing the Card pays. */
  heatCost: number
  /** What it would cost one rung further up, so the reward for coming back is legible. */
  nextHeatCost: number
  /** All three slots, always, earned or not. */
  offers: RefillOffer[]
  /** Grants in your pocket, claimed and not yet spent. At most two per kind. */
  banked: { energy: number; nerve: number }
  /** A refill is refused above this fraction of the bar. See `REFILL_SPEND_CEILING`. */
  spendCeiling: number
}

export interface RefillOutcome {
  kind: 'energy' | 'nerve'
  before: number
  after: number
  heatDelta: number
  source: 'daily' | 'heat_drop' | 'family_gift'
}

/** One item the NPC black market is holding this hour. Dirty cash only, priced above book. */
export interface BlackMarketOffer {
  itemId: string
  name: string
  sub: string
  category: ItemCategory
  rarity: Rarity
  artId: AssetId
  rarityPlateId: AssetId
  tier: number
  baseValue: Cents
  /** Dirty cents. `baseValue * BLACK_MARKET_MARKUP`, so the round trip is always a loss. */
  price: Cents
  stock: number
}

/**
 * What `GET /api/market` returns for one category.
 *
 * `feeBp` is the seller-side fee THIS player would pay right now, and it is on the payload
 * rather than on each listing because it is a property of the viewer, not of the goods.
 */
export interface MarketPayload {
  /** The aisle that was asked for, which is one of the seven books or the merged `all`. */
  category: MarketAisle
  listings: Listing[]
  total: number
  mine: Listing[]
  recent: Fill[]
  insights: PriceStat[]
  black: BlackMarketOffer[]
  inventory: InventoryItem[]
  feeBp: Bp
  /** How much of the whole catalogue is listed, per category, for the rail's counts. */
  counts: Record<string, number>
  blackRefreshAt: number
}

export interface MarketBuyOutcome {
  fills: Fill[]
  spent: Cents
  refunded: Cents
  acquired: { itemId: string; name: string; qty: number }[]
}

export interface MarketSaleOutcome {
  listing: Listing
  /** What the seller will net at the CURRENT fee if it sells at the asking price. */
  netIfSold: Cents
  feeBp: Bp
}

/* -------------------------------------------------------------------- families */

export interface Member {
  playerId: string
  name: string
  handle: string
  portraitId: AssetId
  role: FamilyRole
  roleArtId: AssetId
  level: number
  status: PlayerStatus
  crewId: string | null
  crew: string
  /**
   * RISK-BEARING contribution over the rolling week, in dirty cash tithed to the vault.
   *
   * Never a sum of Energy throughput. Energy regenerates 360 a day against a bar of 138 at level
   * 32, so capturing all of it takes 2.6 spaced logins that a script always makes and a human
   * usually does not, and job steps pay at zero Heat and can never be busted, so an alt farming
   * them never surfaces on the Scanner. A contribution metric a script wins by 31 percent is an
   * alt-farm subsidy with a leaderboard on it. See DESIGN-DELTAS 1g.2.
   */
  weekly: Cents
  xp: number
  /** "Era 1, Week 6". Formatted by the server so one clock decides what week it is. */
  since: string
  joinedAt: number
  /** Zero once probation is served. On Ice until then: no vault, no vote, no war, no posts. */
  probationUntil: number
  /** True when a sponsor vouched for this member, which is what halves probation. */
  sponsored: boolean
  permissions: FamilyPermission[]
  note: string
  me: boolean
}

export interface FamilyCrew {
  crewId: string
  name: string
  icon: string
  on: number
  cap: number
  /** The Capo's display name, or the empty string when the crew has no Capo. */
  lead: string
  leadId: string | null
  c: string
}

export interface FamilyOverview {
  familyId: string
  name: string
  tag: string
  /**
   * The claimed crest, or the empty string for an UNCRESTED family.
   *
   * Fifteen `families.crests.*` assets exist against a design that expects far more than fifteen
   * families, so a crest is a scarce object rather than a founding choice. Empty renders the
   * plain steel plate. Which acquisition rule applies is unruled: see DESIGN-DELTAS section 2
   * item 3, `crest-acquisition-and-family-count`.
   */
  crestId: AssetId
  sealId: AssetId
  headerId: AssetId
  motto: string
  bossId: string
  bossName: string
  memberCount: number
  memberCap: number
  /**
   * Two tanks, never one.
   *
   * The tithe deposits DIRTY and every family sink (upgrades, Books, territory upkeep) spends
   * CLEAN, so a single-balance treasury cannot say which of the two a withdrawal took and the
   * money supply stops reconciling at the family boundary. Phase 5's ledger is per currency for
   * exactly this reason.
   */
  treasuryDirty: Cents
  treasuryClean: Cents
  treasuryCap: Cents
  /** Leader-set, 4 to 12 percent of every member's dirty income. GAME-DESIGN 8.2. */
  tithePct: number
  /** Books level, 1 to 16. Drives the territory income skim. Phase 7 spends it. */
  books: number
  blocksHeld: number
  incomeDaily: Cents
  upkeepDaily: Cents
  netDaily: Cents
  warWins: number
  warLosses: number
  power: number
  recruiting: boolean
  minLevel: number
  foundedAt: number
  myRole: FamilyRole | null
  myPermissions: FamilyPermission[]
  /** Zero when the viewer is off probation or is not a member. */
  myProbationUntil: number
  /** Members off probation with a live sponsorship. Phase 7's territory gate reads this. */
  sponsoredCount: number
  /** Derived from the two chosen branches. Empty until a branch is taken. */
  perks: { icon: string; text: string; c: string }[]
  crews: FamilyCrew[]
}

/** One row of the family browser: enough to decide whether to knock. */
export interface FamilySummary {
  familyId: string
  name: string
  tag: string
  crestId: AssetId
  motto: string
  memberCount: number
  memberCap: number
  minLevel: number
  recruiting: boolean
  power: number
  blocksHeld: number
  bossName: string
  /** Why this family is closed to the viewer right now, in voice, or null when it is open. */
  lockedReason: string | null
}

export interface Upgrade {
  upgradeId: UpgradeId
  name: string
  icon: string
  lvl: number
  max: number
  /** What the branch does at its CURRENT level, in the game's voice. */
  effect: string
  /** What one more level would do, or null at the cap. */
  nextEffect: string | null
  /** Cost of the NEXT level. Zero at the cap. */
  cost: Cents
  /** Clean cash. GAME-DESIGN 9.5 sink #8, `FAMILY_UPGRADE_SINK`. */
  currency: Currency
  /** True once the family has committed to this branch for the Era. */
  chosen: boolean
  /** Null when the branch can be advanced right now. */
  lockedReason: string | null
  c: string
}

export interface VaultItem {
  itemId: string
  name: string
  qty: number
  note: string
  icon: string
  c: string
}

export interface Application {
  playerId: string
  name: string
  handle: string
  portraitId: AssetId
  level: number
  appliedAt: number
  expiresAt: number
  pitch: string
  /** "Vouched by Denny Coughlin", or "Walk-in, no referral". Never invented client-side. */
  referral: string
}

export interface TreasuryState {
  dirty: Cents
  clean: Cents
  cap: Cents
  tithePct: number
  upkeepDaily: Cents
  incomeDaily: Cents
}

/** One line of the family treasury audit. Corp theft is content and content requires a record. */
export interface TreasuryEntry {
  id: string
  playerId: string | null
  who: string
  currency: Currency
  delta: Cents
  balance: Cents
  reason: string
  at: number
}

export interface ChatLine {
  seq: number
  playerId: string
  who: string
  role: FamilyRole
  portraitId: AssetId
  body: string
  at: number
  me: boolean
}

export interface ChatPage {
  lines: ChatLine[]
  /** Pass back as `beforeSeq` for the previous page, or null at the start of the history. */
  cursor: number | null
}

export interface Announcement {
  id: string
  authorId: string
  who: string
  portraitId: AssetId
  body: string
  pinned: boolean
  at: number
}

/** One row of the who-granted-what log. Every role and permission change writes one, forever. */
export interface FamilyAuditEntry {
  id: string
  actorId: string
  actor: string
  subjectId: string | null
  subject: string
  action: string
  detail: string
  at: number
}

export interface WarEntry {
  warId: string
  when: string
  foe: string
  districtId: DistrictId
  result: 'won' | 'lost' | 'stalemate'
  take: Cents
  lost: number
}

/* ------------------------------------------------------------------ sponsorship */

/**
 * GAME-DESIGN 6.3 calls this "the highest-leverage retention mechanic in the document" and makes
 * time-to-first-sponsor the north-star onboarding metric. It had no storage anywhere until
 * Phase 6.
 */
export type SponsorshipState = 'offered' | 'active' | 'honoured' | 'dissolved' | 'forfeited'

export interface SponsorOffer {
  id: string
  /**
   * ANONYMOUS until accepted. The sponsee sees "A Capo in Dockside has vouched for you" and
   * learns the name only on acceptance. FFXIV's Novice Network named the offerer up front and
   * turned the whole thing into a popularity transaction.
   */
  blurb: string
  /** Null while the offer is anonymous. Populated the moment it is accepted. */
  sponsorId: string | null
  sponsorName: string | null
  stake: Cents
  offeredAt: number
  expiresAt: number
}

export interface Sponsorship {
  id: string
  sponsorId: string
  sponsorName: string
  sponseeId: string
  sponseeName: string
  witnessId: string | null
  witnessName: string
  state: SponsorshipState
  stake: Cents
  era: number
  openedAt: number
  resolvedAt: number
}

/* --------------------------------------------------------------------- rank */

/**
 * The Capo gate is six conditions and GAME-DESIGN 6.3 says the Rank screen shows the checklist,
 * so the checklist is the wire type rather than a boolean.
 *
 * "An unsponsored player at day 7 is capped out of half the game AND KNOWS IT, because the Rank
 * screen shows the checklist." A gate a player cannot see is a gate they cannot plan around,
 * which is the failure the whole published-number pitch exists to avoid.
 */
/**
 * `'seat'` and `'appointed'` were members here and were emitted by nothing.
 *
 * They were the Underboss and Boss rungs, and those two are chairs rather than rungs: `rankGate`
 * takes an `EarnedRank` and returns `[]` above Capo, and `nextEarnedRank` cannot name them, so
 * the checklist has no surface on which either id could ever appear. Removed rather than
 * implemented, because a union member nothing produces is a promise the type makes and the code
 * does not keep, and because the chairs are gated where chairs are gated: `setRole` reads
 * `RANK_UNDERBOSS_LEVEL` and the Boss seat is taken rather than handed out.
 *
 * If Phase 7's succession work gives the chairs a checklist of their own, the ids come back with
 * a producer attached.
 */
export type RankRequirementId =
  | 'level'
  | 'vouch'
  | 'contract_helper'
  | 'mastery'
  | 'heat_clear'
  | 'clean_held'
  | 'membership'

export interface RankRequirement {
  id: RankRequirementId
  /** In voice, and it states the requirement rather than showing a padlock. */
  label: string
  met: boolean
  /**
   * The two numbers the checklist prints. Both are already in the unit the label names, so the
   * client formats them and never derives them. `null` for a condition that is not a quantity.
   */
  have: number | null
  need: number | null
  /**
   * True for a condition the game cannot yet decide because the system behind it is not built.
   * It is NOT counted as met and it is NOT counted as unmet: it is stated. Today the only one is
   * the Soldier gate's Contract, because Contracts do not exist. See docs/CONTENT-GAPS.md.
   */
  deferred: boolean
}

/**
 * The vouch offered by the house when nobody else will: GAME-DESIGN 6.3's NPC Sponsor of Record.
 *
 * It is deliberately worse than a human vouch in four separate ways and it carries a permanent
 * Rap Sheet line nobody wants. "It is a floor, not a path."
 */
export interface HouseVouchOffer {
  /** `npc:<sponsor id>`, so `isHouseVouch` is a prefix test and never a join. */
  sponsorId: string
  name: string
  seat: string
  districtId: DistrictId
  portraitId: AssetId
  sealId: AssetId
  /** Authored in `content/sponsors.json`. Empty is not a state this type can be in. */
  blurb: string
  /**
   * What the house's vouch is worth, in the house's own words. `content/house.json` `offer`.
   *
   * On the wire because it was authored, validated and bundled on every build and read by nothing,
   * while the paragraph a player actually read was typed into `Families.tsx`: canon invented in
   * code, in the one place an authored line already existed. The invented version also advertised
   * the co-active Heat bonus by negation ("no shared cover") and no co-active bonus exists
   * anywhere in the tree, so it named a reward the game does not pay.
   */
  terms: string
}

export interface RankState {
  rank: Rank
  nextRank: Rank | null
  /** Every condition on the NEXT rung, met or not, in the order GAME-DESIGN 6.1 states them. */
  requirements: RankRequirement[]
  canPromote: boolean
  /**
   * The house's offer, when the player is eligible for one and their district has a seat.
   * Null when they already hold a vouch, when the Era is younger than the floor, or when the
   * district's pairing is unauthored, in which case `houseLocked` says which in voice.
   */
  houseOffer: HouseVouchOffer | null
  houseLocked: string | null
  /** True once any vouch is on file for this Era, house or human. */
  vouched: boolean
  /** The permanent Rap Sheet line this player's vouch wrote, or the empty string. */
  vouchOutcome: string
  /** PHASE 24. The Line drawn, or '' while none is. */
  line: Line
  /** PHASE 24. True when the player is Soldier or better and has not drawn one yet. */
  lineOpen: boolean
}

export interface SponsorState {
  /** 2 slots at Capo, 3 at Underboss and Boss. Consumed on offer, released on resolution. */
  slots: number
  slotsUsed: number
  /** `250,000 * (1 + era_week * 0.15)` clean, held in escrow, returned at 115 percent. */
  stakeNow: Cents
  /** Why this player cannot vouch for anyone right now, in voice, or null. */
  lockedReason: string | null
  open: Sponsorship[]
  /** The offer waiting on THIS player, if any. Anonymous until accepted. */
  incoming: SponsorOffer | null
  /** The edge that made this player, once it exists. Written to both Rap Sheets forever. */
  mine: Sponsorship | null
}

/** Everything the Families screen renders in one read. */
export interface FamilyPayload {
  overview: FamilyOverview | null
  roster: Member[]
  applications: Application[]
  upgrades: Upgrade[]
  vault: VaultItem[]
  announcements: Announcement[]
  warLog: WarEntry[]
  chat: ChatLine[]
  treasuryLog: TreasuryEntry[]
  audit: FamilyAuditEntry[]
  sponsor: SponsorState
  /** Populated only when the viewer has no family: the browser, and their own application. */
  browse: FamilySummary[]
  myApplication: { familyId: string; name: string; appliedAt: number; expiresAt: number } | null
  /** `wss://.../ws/family/{id}?since=` once the viewer is a member. Null otherwise. */
  socketPath: string | null
}

/* -------------------------------------------------------------------- territory */

export interface Block {
  blockId: string
  name: string
  districtId: DistrictId
  /** One of the 24 `city.blocks.*` artworks. Sixteen blocks reuse a type, which is correct. */
  artId: AssetId
  /** One of the six `territory.states.*` plates. There is no `allied` plate. See `owner`. */
  stateArtId: AssetId
  /** 1 starter, 2 mid, 3 prime. A property of the block TYPE. DESIGN-DELTAS 1m.6. */
  tier: 1 | 2 | 3
  /**
   * Daily clean income before the Books skim, derived from `tier` and never authored.
   * `independentOrigin` blocks pay 70 percent of it, permanently, because 8.3's reduction follows
   * the ground rather than the holder.
   */
  income: Cents
  independentOrigin: boolean
  /** Tribal Wars Loyalty, 0 to 100. Capture at 0, resets to 25. Regenerates only while HELD. */
  control: number
  /** 0 to 4: Open, Watched, Shuttered, Barred, Sealed. GAME-DESIGN 8.4. */
  fortTier: number
  fortTierName: string
  /**
   * The VIEWER's relation to the holder, computed per reader and never stored.
   *
   * `allied` is in this union and is not in the database, which is the whole point of 1m.4: a
   * block is owned by a family or by nobody, and whether that family is your ally is a fact about
   * you. The six stored states each have an artwork; `allied` reuses the owned plate with a
   * different tint, which is a rendering decision rather than a stored value.
   */
  owner: DistrictState['control']
  ownerFamilyId: string | null
  ownerFamilyName: string | null
  ownerCrestId: AssetId | null
  capturedAt: number
  lockedUntil: number
  /**
   * The named economic standing effect a prime block carries for its holder, or null. Seven
   * blocks carry one and thirty-three do not. DESIGN-DELTAS 1j.11 and 1n.7.
   *
   * The name and the sentence are authored in CONTENT-BIBLE 2.3 and arrive from `content/`. No
   * screen types either of them, which the canon check enforces against a second loop over
   * `blocks.json` added for exactly this field.
   */
  standingEffect: { key: string; name: string; effect: string } | null
  /** When a capture finalises. 0 when the block is not Held Under Siege. 8.6 rule 3. */
  siegeUntil: number
  /** Who may take the one discounted counter-attempt inside the siege window, or null. */
  siegePrevFamilyId: string | null
  /**
   * WHAT A RAID ON THIS BLOCK COSTS AND DOES, published before the tap. Null when the viewer has
   * no family, because there is nobody for it to cost.
   *
   * 1j.12's rule is to publish everything a userscript would compute, and this is that rule
   * applied to the one action in the game whose outcome a player would otherwise have to derive
   * by hand from the fortification tier.
   */
  raid: {
    /** Nerve, including the Beat's `raidNerve` multiplier in the Hot district. */
    nerve: number
    /** `5 * fortTier`, subtracted from the roll. */
    absorb: number
    minControl: number
    maxControl: number
    /** The chance the door holds entirely and a fortification tier comes off instead. */
    absorbedBp: Bp
  } | null
}

/** A declaration, and the three windows a defender may still answer into. 1m.2. */
export interface WarDeclaration {
  warId: string
  blockId: string
  blockName: string
  attackerFamilyId: string
  attackerFamilyName: string
  defenderFamilyId: string | null
  defenderFamilyName: string | null
  /** The window the attacker struck OUT at declaration. The defender picks from the other three. */
  struckSlot: number
  /** `WarWindow.index`, or -1 until the defender answers. */
  windowIndex: number
  windowLabel: string
  state: 'declared' | 'answered' | 'resolved' | 'expired'
  declaredAt: number
  /** Populated only while the viewer is the defender and has not answered. */
  answerable: { index: number; slot: number; label: string; startsAt: number; endsAt: number }[]
}

/** One side's pooled action on one block. GAME-DESIGN 8.6's retention lever. */
export interface OperationSummary {
  opId: string
  blockId: string
  familyId: string
  familyName: string
  leaderId: string
  leaderName: string
  state: 'open' | 'resolved'
  contributors: number
  /** Nerve in the pool. A commit SCORES when it is made, so this is not the score. 1n.6. */
  nervePooled: number
  /** True when the viewer has already put their tap in. */
  youAreIn: boolean
  /** The window's close. An Operation nobody triggers goes off at the bell. */
  expiresAt: number
  knocked: number
}

/** `GET /api/territory/board/{blockId}`. What the War Room reads, live. */
export interface WarBoard {
  blockId: string
  blockName: string
  windowIndex: number
  windowLabel: string
  phase: 'declared' | 'mustering' | 'active' | 'aftermath' | 'closed' | 'quiet'
  control: number
  fortTier: number
  /** The viewer's side score, the holder's, and the number the challenger has to reach. */
  yourScore: number
  holderScore: number
  flipAt: number
  /** Presence, five per side per block. `used` is the viewer's side. 1j.11, 1n.1. */
  slotsUsed: number
  slotsCap: number
  youHoldASlot: boolean
  scoreboard: { familyId: string; familyName: string; playerId: string; score: number }[]
  operations: OperationSummary[]
  war: WarDeclaration | null
}

/** `GET /api/territory`. The whole map, because the whole map is forty rows. */
export interface TerritoryPayload {
  blocks: Block[]
  /** The viewer's own family holdings and what they cost, or null if they are unaffiliated. */
  mine: {
    familyId: string
    blocksHeld: number
    books: number
    /** `upkeepDaily(blocksHeld)`, superlinear, charged against the CURRENT holding. */
    upkeepDaily: Cents
    /** Gross daily income across held blocks, after the Books skim. */
    incomeDaily: Cents
    /** Income minus upkeep. Negative past thirteen blocks, and the screen says so. */
    netDaily: Cents
    /** `territorySkim(blocksHeld, books)` as basis points, so the screen prints what is charged. */
    skimBp: Bp
    /**
     * WHAT IS IN THE TILL RIGHT NOW, on the front-business shape: an anchor per block, an hourly
     * rate, a sixty hour ceiling, and a collect that stamps the anchors forward. 1n.3.
     */
    till: Cents
    tillHours: number
    /** True once a block has stopped generating. The ceiling punishes five days away, not two. */
    tillCapped: boolean
    /** Accrued and unpaid upkeep, exact to the millisecond and settled at the collect. 1n.2. */
    upkeepOwed: Cents
    /** Consecutive daily reckonings failed. 8.5's administrative jeopardy, made public. */
    upkeepMissed: number
    /** The standing effects the family currently holds, by key. Seven exist. */
    effects: string[]
    /** The next Books level and what it costs, or null at 16. 1n.8. */
    booksNext: { level: number; cost: Cents } | null
  } | null
  /**
   * Why the viewer's family cannot take ground yet, in voice, or null if it can. 1m.1: the first
   * block needs twelve ranked members off probation and every block after it needs twelve
   * sponsored.
   */
  gate: { open: boolean; reason: string; have: number; need: number } | null
  /** The next four War Windows, derived from the clock. No Durable Object was read for this. */
  windows: { index: number; slot: number; label: string; startsAt: number; endsAt: number }[]
  /** The window open right now, or null. Null 160 hours out of every 168. */
  openWindow: number | null
  /**
   * Every live declaration in the city, which is a handful and is public by design: GAME-DESIGN
   * 8.4 puts a declared block into Contested "immediately and publicly, with a Scanner feed entry
   * visible to everyone, not just the two Families".
   */
  wars: WarDeclaration[]
}

/* -------------------------------------------------------------------- contracts */

export interface Contract {
  id: string
  track: 'sponsor' | 'player' | 'family'
  type: ContractType
  typeIconId: AssetId
  tier: number
  title: string
  pitch: string
  brief: string
  risk: Risk
  cash: Cents
  xp: number
  heat: number
  sponsorId: string | null
  sponsorName: string
  sponsorArtId: AssetId
  sceneId: AssetId
  sealId: AssetId
  dossierId: AssetId
  districtId: DistrictId
  expiresAt: number
  target: {
    name: string
    seen: string
    security: string
    ties: string
    intel: string
  } | null
}

export interface ActiveContract {
  id: string
  title: string
  type: ContractType
  detail: string
  place: string
  expiresAt: number
  sceneId: AssetId
  tint: string
}

/* ------------------------------------------------------------------ PvP layer */

export interface BeefRow {
  otherId: string
  otherHandle: string
  state: BeefState
  stateIconId: AssetId
  scoreMe: number
  scoreThem: number
  /*
   * THE EIGHT GRUDGE CODES USED TO BE HERE, AS `codes: string[]` COMMENTED
   * `LC GH SY LR HJ TB TR BD`, AND THIS IS 1m.9's SITUATION EXACTLY.
   *
   * Those two-letter labels existed in one place in the whole repository, the `BEEF` record under
   * `game/src/screens/Hitlist.tsx`'s own mock-data banner. They had no artwork, no
   * `content/` file and no GAME-DESIGN section. `docs/DATA-MODEL.md` then adopted them as canon
   * for a database CHECK, 1j.12 deleted them "wherever they are authored", the screen's header
   * says they are deleted, both `beef` definitions in DATA-MODEL carry a superseded note, and the
   * WIRE TYPE outlived all of it because a type outlives the check it came from.
   *
   * The replacement is server-set and points into `content/causes.json`. Two properties the codes
   * could not have: a cause is authored content and is reviewable like every other string in
   * `content/`, and a server-set id cannot be spoofed by a client that was previously free to post
   * any two-letter code it liked into a JSON array.
   */
  openedCauseId: string | null
  lastCauseId: string | null
  openedAt: number
  updatedAt: number
  sitdownUntil: number
}

export interface BountyRow {
  id: string
  targetId: string
  targetName: string
  targetLevel: number
  targetArtId: AssetId
  posterHandle: string
  amount: Cents
  tier: 1 | 2 | 3 | 4 | 5 | 6
  tierIconId: AssetId
  districtId: DistrictId
  heatTier: 1 | 2 | 3 | 4
  /* `beef: string[]` was here and it was the same eight grudge codes under a second name. */
  note: string
  postedAt: number
  expiresAt: number
}

export interface PvpOutcome {
  kind: PvpOutcomeKind
  artId: AssetId
  intentIconId: AssetId
  headline: string
  body: string
  stolen: Cents
  hospitalUntil: number | null
  heatDeltaMe: number
  heatDeltaThem: number
  beefAfter: BeefRow
  /** `payout` rather than `paid`: the latter reads as a purchase, and one lint rule says so. */
  /*
   * `pending` is true when the claim was FILED and has not resolved yet, which is the normal case
   * and not an edge one: 1j.12's claim resolves 90 seconds after the hospitalization and shares
   * pro rata among everybody inside that window, so at the moment of the punch there is no payout
   * to report and reporting zero would read as "you got nothing".
   */
  bountyClaimed: { id: string; payout: Cents; burned: Cents; pending: boolean } | null
  /** The published win threshold this roll was compared against. */
  winBp: Bp
  rollId: string
  /**
   * The word of the Era keystream this fight drew from, and -1 for a roll that did not come from
   * it. Published for the same reason `crime_log.roll_word` is stored: at the Era close ceremony
   * the secret is revealed and a player recomputes their own history row by row. A fight that was
   * pinned for support says so rather than quietly passing as one that was rolled.
   */
  rollWord: number
}

/**
 * One rival, as the Fight List and the profile publish them. DESIGN-DELTAS 1o.2.
 *
 * WHAT IS AND IS NOT ON THIS TYPE IS THE RULING. `gearPower` is gear-derived and gear-derived
 * only, so 7.2's "hidden allocation, visible gear" is enforced by the shape rather than by a
 * screen remembering to subtract: there is no field here that could leak the level, rank or
 * Family component even by accident. `winBp` and `fairFightBp` ARE published, to every account,
 * always, because a script that logs its own fights recovers both inside a few dozen of them and
 * hiding an empirically recoverable number buys opacity against honest players only.
 */
export interface FightTarget {
  playerId: string
  handle: string
  displayName: string
  portraitId: AssetId
  level: number
  rank: Rank
  /** PHASE 24. The Line they drew, on the row, because it is the one thing two Soldiers differ by. */
  line: Line
  familyId: string | null
  familyName: string | null
  districtId: DistrictId
  /** Gear-derived power ONLY. Never the level-and-rank component, never Family bonuses. */
  gearPower: number
  /** The threshold the server will roll against if the viewer attacks. */
  winBp: Bp
  /** The Fair Fight scalar on everything the fight would yield. */
  fairFightBp: Bp
  /** How many of the viewer's per-target intents are left in the rolling 24 hours. */
  intentsLeft: number
  intentCap: number
  hospitalUntil: number
  shieldUntil: number
  /** True when this rival is inside their own Sleep block right now. */
  asleep: boolean
  /** Live bounties on their head, in dirty cents. */
  bountyTotal: Cents
  /** Whether the viewer may attack, and if not, the reason in voice. Never a bare boolean. */
  attackable: boolean
  refusal: string | null
}

/** The eight off-switches, as a list the player reads. GAME-DESIGN 7.5 rule 5. */
export interface ProtectionPanel {
  standing: boolean
  endsAt: number
  endedBy: string | null
  conditions: { id: string; label: string; met: boolean }[]
}

/** The nominated 6-hour block, and the notice on a change. 7.5 rule 4, DESIGN-DELTAS 1o.6. */
export interface SleepWindow {
  /** UTC start hour of the live block. Always a number: an unnominated account gets a default. */
  startHour: number
  hours: number
  /** True while the account has never nominated and is running on the derived default. */
  isDefault: boolean
  /** A filed change that has not taken effect yet, and when it will. -1 for none. */
  pendingHour: number
  pendingAt: number
  /** True while the account still has a write left. See `changesLeft`. */
  changeable: boolean
  /**
   * How many writes of the hour are left this Era, of `SLEEP_MAX_CHANGES`.
   *
   * A boolean alone cannot tell a player whether they are spending their last move, and 7.5 rule 4
   * gives them two: the nomination and the one change. A screen that only knows "changeable" has to
   * choose between saying nothing and guessing.
   */
  changesLeft: number
  /** True right now. */
  active: boolean
}

/** One live listing, as the board shows it. */
export interface HitlistRow {
  id: string
  targetId: string
  targetName: string
  posterHandle: string
  amount: Cents
  tier: 1 | 2 | 3 | 4 | 5 | 6
  tierIconId: AssetId
  districtId: DistrictId
  note: string
  postedAt: number
  expiresAt: number
  /** You cannot claim a bounty you placed, and the board says so rather than springing it. */
  mine: boolean
  onMe: boolean
}

/**
 * `GET /api/pvp/hitlist`. The rules travel WITH the rows, which is not decoration.
 *
 * Every number on `rules` is one a player would otherwise have to find in a wiki: the fee, the
 * burn, the expiry, the 90 second claim window, the two concurrent bounties per head, the four
 * listings per lister and the two-rank reach. A board that hides its own arithmetic is a board
 * that gets a spreadsheet written about it.
 */
export interface HitlistPayload {
  rows: HitlistRow[]
  rules: {
    feeBp: Bp
    expiryBurnBp: Bp
    hours: number
    claimWindowMs: number
    maxPerTarget: number
    maxPerPoster: number
    rankReach: number
  }
  /** What is on YOUR head, which is the row that changes how the whole screen reads. */
  onMe: { id: string; amount: Cents; expiresAt: number }[]
  dirty: Cents
  token: string | null
}

/** `GET /api/pvp`: the Fight List, bracketed at 12 levels. GAME-DESIGN 7.7. */
export interface FightListPayload {
  targets: FightTarget[]
  /** The bracket the list was drawn from, published so the emptiness of a list is explicable. */
  bracket: { from: number; to: number }
  protection: ProtectionPanel
  sleep: SleepWindow
  /** The viewer's own gear power, so the card comparison has a left-hand side. */
  myGearPower: number
  intents: { id: PvpIntent; nerve: number; iconId: AssetId; label: string; blurb: string }[]
  beefs: BeefRow[]
  ambush: { againstId: string; againstHandle: string; expiresAt: number } | null
  hospitalUntil: number
  token: string | null
}

/* -------------------------------------------------------------------- scanner */

export interface ScannerLine {
  seq: number
  channel: 'ch12' | 'ch04' | 'ch09'
  kind: ScannerKind
  range: 'block' | 'district' | 'wide'
  who: string
  text: string
  hot: boolean
  districtId: DistrictId
  subjectId: string | null
  at: number
}

export interface WantedRow {
  playerId: string
  handle: string
  rank: Rank
  portraitId: AssetId
  heat: number
  districtId: DistrictId
  /**
   * The wall-clock instant of the player's last action, or 0 when the band does not publish it.
   *
   * An INSTANT and not an elapsed count. The field was documented as "minutes since last action"
   * and always carried `last_seen_at` verbatim; a client rendering minutes off it would have shown
   * a number in the tens of billions. It is an instant for the same reason every other timestamp
   * on the wire is: the client derives elapsed time from `serverNowMs`, never from its own clock.
   */
  seenAt: number
  bountyTotal: Cents
  /** The Scanner never publishes an exact dirty balance. Six bands, six bounty tier icons. */
  dirtyBand: 1 | 2 | 3 | 4 | 5 | 6
}

/**
 * The twelve authored alert types, CONTENT-BIBLE 10.2, one per district.
 *
 * `kind` was a bare `string` from Phase 0 until Phase 9, which is the shape 1m.9 deleted from
 * `IntelRow` and 1o.12 deleted from `BeefRow`. It is NOT deleted here, and 1p.6 records why the
 * third instance of a pattern got the opposite ruling: those two were residue of rulings that had
 * already deleted the system underneath them, and this one is waiting on a system that is fully
 * authored and simply unbuilt. 10.2 specifies all twelve with a duration, a headline and a
 * mechanical effect, and `docs/DATA-MODEL.md:1552` carries the table.
 *
 * **The twelve EFFECTS are not built and `docs/CONTENT-GAPS.md` says which subsystem each needs.**
 * They are twelve modifiers across six subsystems, one of which is Contracts, which exists in no
 * migration. Nothing writes an `AlertRow` today.
 */
export const ALERT_KINDS = [
  'harbor_inspection',
  'raid_aftermath',
  'patrol_buildup',
  'financial_crimes_van',
  'rail_yard_search',
  'undercover_activity',
  'roadblock',
  'citywide_sweep',
  'jail_intake',
  'courthouse_lockdown',
  'helicopter_searchlight',
  'warehouse_seizure',
] as const
export type AlertKind = (typeof ALERT_KINDS)[number]

/**
 * `GET /api/scanner`: the feed, the wanted list and the district pressure, in one read.
 *
 * Three shapes and one payload, because they are one screen. The feed is a D1 query, the wanted
 * list and the pressure are a KV snapshot the minute cron regenerates, and the split is ruled at
 * DESIGN-DELTAS 1p.5.
 */
export interface ScannerPayload {
  /** Newest first, each row already carrying the authored line it renders. */
  feed: ScannerLine[]
  /**
   * Named targets only. A player below Soldier is counted in `pressure` and named here never,
   * which is 3.7 rule 4 read as it is written: it protects them from being a TARGET, not from
   * being mentioned.
   */
  wanted: WantedRow[]
  /** Watched-or-above per district. A COUNT, with no handles in it, which is 3.2's Watched row. */
  pressure: { districtId: DistrictId; name: string; count: number; band: HeatBand }[]
  /** The Beat's hot district and its authored `scanner.alerts.*` headline card. */
  beat: { districtId: DistrictId; name: string; alertId: string; endsAt: number }
  /** How many accounts are published at all, including the ones that are only a number. */
  watched: number
  /** The Heat at which a player stops being a number and starts being a row. Published. */
  namedFrom: number
  namedBand: HeatBand
  /** When the wanted snapshot was cut. Zero before the first cron of an Era. */
  snapshotAt: number
}

export interface AlertRow {
  id: string
  districtId: DistrictId
  kind: AlertKind
  note: string
  artId: AssetId
  severity: 'low' | 'medium' | 'high'
  startsAt: number
  endsAt: number
}

/* ----------------------------------------------------------------- RICO and the ladder */

/**
 * One rung, as the player reads it. The NAME and the LINE are authored in CONTENT-BIBLE 10.3 and
 * arrive from `content/warrants.json`; the THRESHOLD is `WARRANT_LADDER`, which is what the server
 * compares against. Neither is typed into a screen.
 */
export interface WarrantRung {
  kind: WarrantKind
  name: string
  artId: AssetId
  ip: number
  line: string
  /** Whether this rung is outstanding right now. The whole ladder ships, reached or not. */
  reached: boolean
}

/**
 * The Sealed Indictment, after it has fired. GAME-DESIGN 3.8, DESIGN-DELTAS 1p.4.
 *
 * The harshest single event in the game, and the one whose whole design rests on being READ
 * BEFORE IT HAPPENS. Everything here is also computable in advance and is published on the RICO
 * panel as `SeizurePreview`, from the same function, so the preview and the event cannot disagree.
 */
export interface IndictmentEvent {
  /** 40 percent of dirty ON HAND, after whatever the triggering action already took. */
  dirtySeized: Cents
  /** What the goods were worth. NOT a ledger movement: an item-to-nothing event writes no row. */
  goodsSeized: Cents
  /** Itemised, because "a quarter of your things" is not something a player can check. */
  items: { itemId: string; name: string; qty: number }[]
  /** Four hours, or the existing sentence if it somehow runs longer. Extended, never summed. */
  jailUntil: number
  legendLost: number
  /** Set to 45, and the meter to zero. Both are the START of the climb back, not a punishment. */
  heatAfter: number
}

/**
 * What it WOULD take, right now, if the meter reached 100 this second.
 *
 * The whole reason the Sealed Indictment is a mechanic and not a trap. Computed from the same
 * `seizeGoods` the execution calls, so the itemised list a player reads before it fires is the
 * list it takes.
 */
export interface SeizurePreview {
  dirtyOnHand: Cents
  /** `floor(dirtyOnHand * 0.40)`. */
  dirtyAtRisk: Cents
  /** Every unequipped contraband and gear stack, valued. Equipped gear is never eligible. */
  holdings: Cents
  /** `floor(holdings * 0.25)`, which is what the seizure aims at. */
  goodsTarget: Cents
  /** What it would actually take, at or just over the target, because units are indivisible. */
  goodsAtRisk: Cents
  items: { itemId: string; name: string; artId: AssetId; qty: number; value: Cents }[]
  jailMs: number
  legendLost: number
}

/**
 * The counterplay surface, and it ships in the same stage as the thing it is counterplay FOR.
 *
 * GAME-DESIGN 3.8 already specifies all of it: the meter is visible, Heat under 75 drains it at 6
 * an hour, and the Precinct Captain is the way to buy the Heat down. None of that was published to
 * a client before Phase 9: `indictmentProgress` was computed and shipped on every snapshot and no
 * screen read it, so a player's Indictment Progress could climb to 100 with nothing anywhere
 * telling them it existed.
 */
export interface RicoPanel {
  /** 0 to 100, settled. */
  progress: number
  /** The rung outstanding, or null below IP 15. */
  warrant: WarrantKind | null
  /** All six, in order, each with its authored name and line and whether it is reached. */
  ladder: WarrantRung[]
  /** The next rung and how much IP away it is, or null at the top. */
  next: { kind: WarrantKind; ip: number; away: number } | null
  /** IP gained per Energy-spending or PvP action at the CURRENT Heat. Zero below 90. */
  gainPerAction: number
  /**
   * The exact number the Sealed Indictment would take, itemised, right now.
   *
   * "Ship no punishment without its counterplay in the same stage" is the one scoping rule the
   * Phase 9 kickoff did not delegate, and this field is what satisfies it: a player can read the
   * cash figure, the item list and the four hours long before the meter is anywhere near 100.
   */
  seizure: SeizurePreview
  /** True while Heat is under 75, which is the only state in which the meter falls. */
  draining: boolean
  /** Points an hour it falls at while draining. */
  drainPerHour: number
  /** Heat at or above this is RICO Watch, and under this the meter drains. Published, not implied. */
  ricoAt: number
  drainBelow: number
}

/* ------------------------------------------------------------------- war room */

/*
 * `WarBoard` USED TO BE HERE AND IT WAS THE MOCKUP'S. DESIGN-DELTAS 1k.11 and 1m.5.
 *
 * It carried `side: 'a' | 'b'`, `stakeDistrict`, `strategy`, a `tasks` array and a `stock` array
 * with a `c` field holding a CSS colour, which is the exact signature of a type written from a
 * drawing rather than from a rule. `side` is the same two-sided model the deleted `squad` table
 * assumed; a war is between a family and a BLOCK, and Independent ground has no second side at
 * all. The real one is defined beside `TerritoryPayload` above and it is keyed on the block,
 * because the block is what the fight is over and what the storage is authoritative for.
 */

export interface SabotageOffer {
  type: SabotageType
  name: string
  iconId: AssetId
  /** 6 of the 10 sabotage types have a full still. The other four render icon-only. */
  sceneId: AssetId | null
  districtId: DistrictId
  risk: Risk
  nerveCost: number
  blurb: string
  availableAt: number
}

export interface SabotageOutcome {
  type: SabotageType
  result: 'success' | 'blocked' | 'caught'
  points: number
  effect: string
  heatDelta: number
  artId: AssetId
}

export interface IntelRow {
  id: string
  /**
   * TWO products, not five. DESIGN-DELTAS 1m.9.
   *
   * This union shipped `loadouts`, `crew_weakness`, `financial`, `shift_roster` and
   * `stockpile_count`, which are the five products the deleted `WarRoom.tsx` mockup drew.
   * `docs/DATA-MODEL.md` withdrew the matching CHECK constraint in July and nobody removed the
   * wire type it had already produced, so a mockup's invented canon was a shipped contract.
   *
   * GAME-DESIGN 8.6 prices Espionage at 3 Nerve and names what it reveals: "stockpiles, Control
   * values, or nominated slots". Nominated slots died with the nomination rule at 1j.11, and
   * `loadouts` describes combat stats that 1f.4 rules do not exist. Two survive.
   */
  kind: 'count' | 'ledger'
  name: string
  note: string
  icon: string
  readyAt: number
  body: string | null
}

/*
 * `Squad` USED TO BE HERE AND THERE IS NO SQUAD. DESIGN-DELTAS 1m.5.
 *
 * The interface was six fields lifted from the `SQUADS` array in `game/src/screens/WarRoom.tsx`,
 * an unrouted mockup removed at 1k.11, and `docs/DATA-MODEL.md` had already adopted the same
 * array as the source for a database table with an arity of six. The word `squad` appears zero
 * times in `docs/GAME-DESIGN.md`. GAME-DESIGN 8.6 does specify the unit of in-window
 * coordination and it is not a squad: it is an Operation, declared by a Capo on a block, opted
 * into with one click, resolving as one pooled action at a leader-triggered moment.
 *
 * Phase 7 was asked to decide whether a squad exists at all. It does not.
 */

/* --------------------------------------------------------------------- ledger */

export interface Reward {
  id: string
  name: string
  artId: AssetId
  qty: string | null
  rarity: Rarity
}

export interface TierRow {
  t: number
  free: Reward
  prem: Reward
  state: 'claimed' | 'ready' | 'locked'
}

export interface LegendRow {
  n: number
  playerId: string
  name: string
  family: string
  legend: number
  eras: number
  me: boolean
}

export interface HallRow {
  era: number
  eraName: string
  districtId: DistrictId
  playerId: string
  handle: string
  familyName: string
  crestId: AssetId
  at: number
}

/* ------------------------------------------------------------------- intents */

/**
 * Every action token is bound to exactly one of these. A token minted for `crime.commit`
 * cannot be replayed against `market.buy`.
 */
export type ActionIntent =
  | 'player.district'
  | 'crime.commit'
  | 'job.run'
  | 'job.claim'
  | 'money.launder'
  | 'money.refill'
  /** Buying a rung of the laundering ladder. Its own intent because it moves millions. */
  | 'money.tier'
  /** Selling contraband to the NPC fence for dirty cash. */
  | 'money.fence'
  | 'heat.cooloff'
  /*
   * The Ward, `docs/PHASE-6B.md` section 5. Four verbs, four intents, because they cost four
   * different things: bail is clean cash, a bust is Nerve, a self-bail is dirty at an escalating
   * multiple, and the Seal is free and reversible. A single `ward.act` would let a token minted
   * for the free one be spent on the one that moves money.
   */
  | 'ward.bail'
  | 'ward.bust'
  | 'ward.selfbail'
  | 'ward.seal'
  | 'market.list'
  | 'market.buy'
  | 'market.cancel'
  /** Buying from the NPC black market, in dirty. Distinct from a player-listing buy. */
  | 'market.black'
  /** The three equipment slots. Distinct from `ledger.equip`, which is cosmetics. */
  | 'gear.equip'
  /** Clean cash into Condition. The largest clean sink in the game. */
  | 'gear.repair'
  /** The Crusher: any item into materials at 8 percent of base, guaranteed. */
  | 'gear.crush'
  /** Taking the till from a front business. */
  | 'business.collect'
  /** PHASE 22. Levelling a front with clean cash and materials. */
  | 'business.upgrade'
  /**
   * Taking the till from the family's ground, which settles the upkeep bill in the same act.
   *
   * It is its OWN intent and not `territory.abandon`, which is what it shipped under in Phase 7B
   * and which was wrong. `withToken` does not validate the intent, so nothing broke; what it does
   * is set `X-Action-Intent` on the response and name the act in every audit trail that reads it.
   * A collect labelled as an abandon is a lie in a log, and the whole point of the one-token-one-
   * intent binding is that the label is the act.
   */
  | 'territory.collect'
  | 'family.apply'
  | 'family.decide'
  | 'family.role'
  | 'family.crew'
  | 'family.kick'
  | 'family.deposit'
  | 'family.withdraw'
  | 'family.upgrade'
  /** One Books level, at the published price. DESIGN-DELTAS 1n.8. Shipped mislabelled; see above. */
  | 'family.books'
  | 'family.announce'
  | 'family.chat'
  | 'family.sponsor'
  | 'family.create'
  /** Walk out. Distinct from `family.kick`, which somebody else does to you. */
  | 'family.leave'
  /** The Boss moving the automatic cut between the 4 and 12 percent bounds. */
  | 'family.tithe'
  /** Granting or revoking one of the ten named permissions. Audit-logged, always. */
  | 'family.grant'
  /** A Nerve refill given to a family member, at 40,000 dirty to the giver. */
  | 'family.gift'
  /** Accepting a vouch, and standing as its witness. Two sides of one ceremony. */
  | 'family.vouch'
  /** Claiming the next rung once its checklist is complete. GAME-DESIGN 6.1. */
  | 'rank.promote'
  /** Taking the house's vouch, which is a floor and not a path. GAME-DESIGN 6.3. */
  | 'rank.house'
  /** PHASE 24. Drawing the Line at Soldier, once an Era. DESIGN-DELTAS 1ae.1. */
  | 'rank.line'
  | 'territory.scout'
  | 'territory.raid'
  | 'territory.defend'
  | 'territory.fortify'
  | 'territory.abandon'
  | 'contract.accept'
  | 'contract.complete'
  | 'contract.abandon'
  | 'contract.reroll'
  | 'pvp.attack'
  | 'pvp.bounty'
  | 'pvp.sitdown'
  /*
   * `pvp.bail` WAS HERE AND IT IS WITHDRAWN, not deferred.
   *
   * The bail route it named was reassigned to Phase 6B and shipped as four `ward.*` intents under
   * a different prefix, which `docs/PHASE-6B.md:545` records. An intent naming a route that exists
   * under another name is worse than a missing one: it reads as a gap somebody still owes.
   */
  | 'pvp.hospital'
  /** Swearing a Vendetta, or accepting the offer of one. 1j.12's consent rule lives on this. */
  | 'pvp.vendetta'
  /** Arming the Ambush, which is a purchase in dirty rather than an attack. GAME-DESIGN 7.3. */
  | 'pvp.ambush'
  /** Nominating the 6-hour Sleep block, which takes effect 72 hours later. 1o.6. */
  | 'pvp.sleep'
  | 'war.declare'
  /** The defender picking which of the remaining three windows it resolves in. 1m.2. */
  | 'war.answer'
  | 'war.sabotage'
  | 'war.intel'
  | 'war.stockpile'
  /**
   * Pooled commit inside a window. GAME-DESIGN 8.6 calls Operations "the most important retention
   * lever in the whole war layer". This replaces `war.squad`, which named a unit that exists
   * nowhere in the design corpus and came from a deleted mockup's array. DESIGN-DELTAS 1m.5.
   */
  | 'war.operation'
  | 'war.strategy'
  | 'war.chat'
  | 'ledger.claim'
  | 'ledger.equip'
  | 'ledger.purchase'
  /**
   * Putting the first-night list away, permanently, on every device. DESIGN-DELTAS 1t.11.
   *
   * Its own intent rather than a preference call, because it is the ONE thing about onboarding
   * that is stored, and a stored decision that arrives without a single-use token is a decision a
   * script can make for somebody. It also puts the dismissal in the anti-cheat timeline, which is
   * mildly interesting: an account that dismisses the tutorial in the same second it is created is
   * a signal, and one that would otherwise be invisible.
   */
  | 'onboarding.dismiss'

export const ACTION_INTENTS = [
  'player.district',
  'crime.commit',
  'job.run',
  'job.claim',
  'money.launder',
  'money.refill',
  'money.tier',
  'money.fence',
  'heat.cooloff',
  'market.list',
  'market.buy',
  'market.cancel',
  'market.black',
  'gear.equip',
  'gear.repair',
  'gear.crush',
  'business.collect',
  'business.upgrade',
  'family.apply',
  'family.decide',
  'family.role',
  'family.crew',
  'family.kick',
  'family.deposit',
  'family.withdraw',
  'family.upgrade',
  'family.announce',
  'family.chat',
  'family.sponsor',
  'family.create',
  'family.leave',
  'family.tithe',
  'family.grant',
  'family.gift',
  'family.vouch',
  'rank.promote',
  'rank.house',
  'rank.line',
  'territory.scout',
  'territory.raid',
  'territory.defend',
  'territory.fortify',
  'territory.abandon',
  'contract.accept',
  'contract.complete',
  'contract.abandon',
  'contract.reroll',
  'pvp.attack',
  'pvp.bounty',
  'pvp.sitdown',
  'pvp.hospital',
  'pvp.vendetta',
  'pvp.ambush',
  'pvp.sleep',
  'war.declare',
  'war.answer',
  'war.sabotage',
  'war.intel',
  'war.stockpile',
  'war.operation',
  'war.strategy',
  'war.chat',
  'ledger.claim',
  'ledger.equip',
  'ledger.purchase',
  'onboarding.dismiss',
  /*
   * THE FOUR WARD INTENTS WERE MISSING FROM THIS ARRAY FOR TWO PHASES, and they are the four that
   * actually work: `server/src/routes/ward.ts` binds all four and `acceptance:phase6` drives
   * every one of them. The union had them and the array did not, so the array was not exhaustive
   * over the type it claims to enumerate.
   *
   * It was not cosmetic. `tools/content/artrefs.mjs` uses this array as its `notIds` set, which is
   * how the content gate tells an ACTION INTENT apart from a dotted-lowercase ASSET ID. A screen
   * that named `ward.bail` in a string would have failed the build with "asset id does not
   * resolve", which is a true statement about the wrong thing and the hardest kind of error to
   * diagnose.
   */
  'ward.bail',
  'ward.bust',
  'ward.selfbail',
  'ward.seal',
  'territory.collect',
  'family.books',
] as const satisfies readonly ActionIntent[]

/**
 * AND THE ARRAY IS EXHAUSTIVE OVER THE UNION, checked by the compiler rather than by a reader.
 *
 * `satisfies readonly ActionIntent[]` above proves every entry is a real intent. It does NOT prove
 * the reverse, which is the direction that actually went wrong: four live intents sat in the union
 * and not in the array, and nothing anywhere noticed. If an intent is added to the union and not
 * to the array, `Exclude` below is no longer `never` and this line fails to compile, naming the
 * missing intent in the error.
 */
type AssertNever<T extends never> = T
export type NoUnlistedIntent = AssertNever<
  Exclude<ActionIntent, (typeof ACTION_INTENTS)[number]>
>

/**
 * The closed reason-code enum. Written at mutation time and the only source of truth for money
 * supply: SUM(delta) is the supply, GROUP BY reason and sign(delta) is faucets and sinks.
 *
 * THE INVARIANT, and it is asserted by `tools/acceptance/phase5.mjs` after a thousand mixed
 * operations: for each currency, the sum of every ledger delta equals the balance. Nothing may
 * move money without writing a row, and no row may be written that does not move money.
 *
 * `LAUNDER_HAIRCUT` is the one member that is DERIVED and never written, because it is not a
 * balance movement: laundering writes `LAUNDER_IN` against dirty and `LAUNDER_OUT` against
 * clean, and the haircut is exactly the gap between the two tanks. Writing it as a third row
 * would double-count the destruction and break the invariant above. The economy dashboard
 * computes it as `-SUM(LAUNDER_IN) - SUM(LAUNDER_OUT)`, which is the one-line query DATA-MODEL
 * 6.2 describes. It stays in the enum because it is the name of a line on that dashboard.
 */
export const LEDGER_FAUCETS = [
  'CRIME_PAYOUT',
  'JOB_PAYOUT',
  'CONTRACT_PAYOUT',
  'PVP_MUG_GAIN',
  'BOUNTY_CLAIM',
  'TERRITORY_INCOME',
  'BUSINESS_INCOME',
  'MARKET_SALE',
  'TREASURY_PAYOUT',
  'ERA_STARTER_GRANT',
  'LEDGER_TIER_CASH',
  'LAUNDER_OUT',
  /**
   * The 15 percent premium on a returned sponsorship stake, and ONLY the premium.
   *
   * The stake itself is held in escrow rather than debited, exactly as a market hold is, so the
   * principal never leaves the ledger and returning it writes no row. Writing the whole 115
   * percent back as a faucet would mint the principal a second time and break the one invariant
   * `tools/acceptance/phase5.mjs` checks by walking a real player's history.
   */
  'SPONSOR_STAKE_RETURN',
] as const

export const LEDGER_SINKS = [
  'LAUNDER_IN',
  'LAUNDER_HAIRCUT',
  /**
   * Buying a rung of the laundering ladder: 900k, 4.2M, 18M clean. GAME-DESIGN 9.5 calls this
   * `INFRA_SINK` and the implemented enum simply did not carry it, so the largest single clean
   * purchase in the game had nowhere to be recorded. Recorded in docs/DESIGN-DELTAS.md.
   */
  'LAUNDER_INFRA',
  'FENCE_FEE',
  'BRIBE_PRECINCT',
  'MARKET_FEE',
  'MARKET_BUY',
  'GEAR_REPAIR',
  'TERRITORY_UPKEEP',
  'TREASURY_DEPOSIT',
  'FAMILY_UPGRADE',
  /** PHASE 22. A front's next level, in clean. The materials half writes no row, as the Crusher. */
  'FRONT_UPGRADE',
  'BOUNTY_POST',
  'BOUNTY_BURN',
  'PVP_MUG_LOSS',
  'RICO_SEIZURE',
  'BAIL',
  'HOSPITAL_FEE',
  'REFILL_PURCHASE',
  'SABOTAGE_COST',
  'INTEL_COST',
  'STOCKPILE_BUY',
  'CONTRACT_FEE',
  'SITDOWN_TRIBUTE',
  'BUST_CONFISCATION',
  'COMMENDATION',
  'VENDETTA',
  'AMBUSH',
  'REFILL_GIFT',
  'SCORE_STAKE',
  'SPONSOR_STAKE',
  /**
   * The automatic cut of a member's dirty income, 4 to 12 percent, set by the Boss.
   *
   * A row on the MEMBER's ledger, written in the same await-free run as the payout it is taken
   * from, so a player can add up their own history and see exactly what the family took and
   * when. GAME-DESIGN 9.5's sink table does not list the tithe because it is a transfer rather
   * than a burn: the money lands in the family vault and comes back out through
   * `TREASURY_PAYOUT`. It still needs a name, because nothing may move a balance without one.
   */
  'FAMILY_TITHE',
] as const

export type LedgerFaucet = (typeof LEDGER_FAUCETS)[number]
export type LedgerSink = (typeof LEDGER_SINKS)[number]
export type LedgerReason = LedgerFaucet | LedgerSink

/* ------------------------------------------------------------------- the Ledger screen */

/**
 * `GET /api/ledger`: the pass track, the badge wall, the Era record and the standings.
 *
 * Every row on it is either authored content or a database read. The screen it feeds used to be
 * the last one in the game with no server data at all, and what filled the gap was fifteen
 * TypeScript arrays: twelve invented people on the standings board including a sixteenth family
 * that does not exist, seventeen badge names of which one was real, and a $9.99 bundle selling
 * two cosmetics that can only be earned.
 */

/** Where a cosmetic comes from. The three purchasable values are the first three. */
export type CosmeticSource = 'pass_free' | 'pass_premium' | 'direct' | 'earned' | 'default'

export interface LedgerReward {
  id: string
  name: string
  artId: AssetId
  fallbackId: AssetId
  rarity: Rarity
  slot: string
  source: CosmeticSource
  /** CONTENT-BIBLE 9.2's Source cell, verbatim. What the player reads. */
  blurb: string
}

export interface LedgerPassTier {
  tier: number
  /** A reward id, or null for a rung the bible has not authored anything onto. */
  free: string | null
  /*
   * The name of the pass's paid TRACK, which is what CONTENT-BIBLE 9.2 calls it and what
   * `content/pass.json` keys it as, so renaming it here would put the wire out of step with the
   * content file the client reads. It grants a cosmetic id and nothing else: the content gate
   * walks this exact field on every build and refuses anything on it that is not a cosmetic from
   * a purchasable source. This is a track label, not a capability keyed on what somebody paid.
   */
  // ashport-lint-disable-next-line ashport/no-paid-capability -- the pass's paid track label; the gate walks this field and refuses non-cosmetics on it
  premium: string | null
}

export interface LedgerSurface {
  id: string
  kind: 'pass' | 'bundle' | 'direct' | 'subscription' | 'tier_skip'
  label: string
  priceUsd: number
  grants: string[]
  /** True while nothing behind it can take money. Every surface is closed until Phase 12. */
  closed: boolean
}

/**
 * One of the 48 legacy badges, with whether this player holds it.
 *
 * `heldAt` is read from the permanent `title` table, which has no writer yet, so it is null for
 * everybody today and the wall says so rather than inventing a wall full of them. Granting is
 * Phase 10's work, at Era close, which is also when every one of the 48 conditions resolves.
 */
export interface LedgerBadge {
  id: string
  name: string
  artId: AssetId
  group: string
  condition: string
  multiEra: boolean
  heldAt: number | null
}

/** One Era of this player's permanent record, joined to the Era the content names. */
export interface LedgerEraRecord {
  n: number
  name: string
  plateId: AssetId
  legend: number
  rankFinal: string
  levelFinal: number
  familyName: string | null
  passTier: number
  badges: number
  /** True for the Era that is running now, whose record is not final. */
  live: boolean
}

export interface LedgerStanding {
  n: number
  handle: string
  legend: number
  erasPlayed: number
  me: boolean
}

export interface LedgerBoss {
  era: number
  districtId: DistrictId
  handle: string
  familyName: string
  crestId: AssetId
}

/* ------------------------------------------------------------------------- the Ward */

/**
 * `GET /api/ward`: a public, live, citywide list of everybody currently in a cell.
 *
 * The punishment screen is a directory of other real people. That is Torn's jail page,
 * deliberately, and it is the reason the Ward is worth more than a bail button: the first thing a
 * newly busted player sees is that they are not the only one, and the second is a row they could
 * do somebody a favour on.
 *
 * Every price and every chance on a row is computed FOR THE VIEWER and published before the tap.
 */
export interface WardRow {
  playerId: string
  handle: string
  displayName: string
  portraitId: AssetId
  level: number
  familyName: string | null
  crestId: AssetId | null
  /** Minutes left on the sentence, rounded up, as of `serverNowMs`. */
  minutesRemaining: number
  releasesAt: number
  /** What THIS viewer would pay, in clean, with a live Marker already applied. */
  bailPrice: Cents
  /** What THIS viewer's odds are, in basis points, with a live Marker already applied. */
  bustBp: number
  /** Null when the viewer may act. A string, in voice, when they may not. */
  bailLocked: string | null
  bustLocked: string | null
  /** True when this viewer holds a live Marker owed to this person. */
  marker: boolean
  /** True for the viewer's own row, which is pinned to the top of their list. */
  me: boolean
}

/** One 72-hour obligation, from the holder's side. */
export interface MarkerRow {
  id: string
  owedToId: string
  owedToHandle: string
  expiresAt: number
}

/** The weekly Blotter. Ranked by DISTINCT PEOPLE HELPED, never by count and never by money. */
export interface BlotterRow {
  n: number
  handle: string
  /** How many DIFFERENT people this account got out this week. The only ranking key. */
  peopleHelped: number
  me: boolean
}

export interface WardPayload {
  rows: WardRow[]
  /** The viewer's own cell, or null when they are not in one. */
  self: {
    inJail: boolean
    releasesAt: number
    minutesRemaining: number
    sealed: boolean
    /** What buying your own way out costs right now, in DIRTY, at the escalating multiple. */
    selfBailPrice: Cents
    selfBailLocked: string | null
    /** How many times this account has bought its own way out in the trailing 24 hours. */
    selfBailsToday: number
  }
  markers: MarkerRow[]
  blotter: BlotterRow[]
  /** How many distinct people the viewer has helped this Era. Their own Blotter line. */
  peopleHelped: number
}

/** What a bail or a bust answered with. */
export interface WardOutcome {
  kind: 'bail' | 'bust' | 'self'
  landed: boolean
  /** The chance that was published on the button, in basis points. Zero for a bail. */
  chanceBp: number
  /*
   * What it cost, in whichever currency the verb spends: clean for a bail, dirty for a self-bail,
   * nothing for a bust. In-game money moving between an account and a sink, never real money, and
   * the `no-paid-capability` rule is about the second one.
   */
  // ashport-lint-disable-next-line ashport/no-paid-capability -- in-game cash spent on the Ward, not a paying tier
  paid: Cents
  headline: string
  body: string
  /** Set when a failed bust put the buster in a cell. */
  jailUntil: number | null
  /** Set when the rescued player now owes the rescuer a Marker. */
  markerOpened: boolean
  /**
   * True when the rescuer may vouch for this player right now: they are Associate, outside the
   * rescuer's Family, not helped by them before this Era, and the rescuer is Capo or above with a
   * free slot and the stake. GAME-DESIGN:315 says bail exists because "a Boss bailing out a
   * stranger's Associate is exactly the moment a sponsorship starts"; this is that, mechanically.
   */
  canVouch: boolean
  vouchPlayerId: string | null
}

export interface LedgerPayload {
  pass: {
    priceUsd: number
    /** 40. The rungs with nothing authored on them are absent from `tiers` and still counted. */
    tierCount: number
    tiers: LedgerPassTier[]
    /** This player's rung, from `legend_era.pass_tier`. */
    tier: number
    owned: boolean
    /** The authored sentence a closed surface renders. Empty when the till is open. */
    closedLine: string
    /**
     * GAME-DESIGN 12.1's Charter, as ten lines a player reads on the store surface.
     *
     * On the payload rather than typed into the screen, because it is a PUBLIC PROMISE and 12.1
     * calls the page free marketing. `tools/content/validate.mjs` pins every line to a second
     * independent copy in the validator, so the page and the promise cannot drift apart.
     */
    charter: string[]
  }
  rewards: LedgerReward[]
  surfaces: LedgerSurface[]
  badges: LedgerBadge[]
  eras: LedgerEraRecord[]
  standings: LedgerStanding[]
  bosses: LedgerBoss[]
  legend: { era: number; lifetime: number; erasPlayed: number }
  /** Every cosmetic this player owns, and how they got it. Empty until Phase 12 grants one. */
  wardrobe: WardrobeItem[]
  /**
   * Where this account stands against the published spend cap.
   *
   * ON THE LEDGER PAYLOAD RATHER THAN ONLY ON `/api/purchase`, because GAME-DESIGN 12.1 publishes
   * the cap on a public page and Phase 11's Reveal set the precedent that a player can always see
   * the number applied to them. A cap a player only meets by being refused is a cap that takes
   * money to explain itself.
   */
  cap: CapView
}

/* ------------------------------------------------------------------------- onboarding */

/**
 * ONE BEAT OF THE FIRST NIGHT, joined and already decided. `docs/PHASE-6B.md` section 7.
 *
 * `done` is computed on the SERVER against real player state and is not a field the client may
 * set, send back or infer. There is no "I did that" endpoint anywhere: every beat is a count over
 * a table the player's own object already keeps, so a player who never looks at this panel
 * satisfies the same predicates by playing.
 */
export interface FirstNightBeat {
  id: string
  /** The act's name, four of them, so the panel groups without a second lookup. */
  act: string
  title: string
  /** A route id on the rail. The panel sends the player there. */
  where: string
  copy: string
  /** False on every beat in Phase 13. DESIGN-DELTAS 1t.11. */
  forced: boolean
  energyCost: number
  done: boolean
}

export interface FirstNightState {
  beats: FirstNightBeat[]
  currentId: string | null
  done: number
  total: number
  /** Every beat satisfied. NOT the same as `dismissed`, and never recorded as one. */
  finished: boolean
  /** The player put it away. Server-recorded, so it is honoured on every device. */
  dismissed: boolean
}

/**
 * `GET /api/next`. The answer to "what do I do now", at two scales, in one read.
 *
 * The first-night beats are the first session and the rank checklist is the first week, and they
 * are one payload because they are one question asked twice. DESIGN-DELTAS 1t.12.
 *
 * The protection checklist is deliberately NOT here. It is eight things that END a new player's
 * immunity, so rendering it in a panel that says what to do next would be instructing somebody to
 * walk into the open. It is a disclosure and it has its own panel.
 */
export interface NextPayload {
  firstNight: FirstNightState
  rank: {
    rank: Rank
    nextRank: Rank | null
    requirements: RankRequirement[]
    canPromote: boolean
    vouched: boolean
    /** PHASE 24. The Line drawn, or '' while none is; and whether one is waiting to be drawn. */
    line: Line
    lineOpen: boolean
  }
}

/* ---------------------------------------------------------------------------- commerce */

/**
 * One cosmetic this player owns.
 *
 * `source` is HOW THEY GOT IT, which is a different question from the catalogue's `source` field
 * (WHERE IT COMES FROM IN THE DESIGN). The same cosmetic legitimately reaches two players by two
 * routes, and the wardrobe is the screen where that distinction is visible.
 */
export interface WardrobeItem {
  cosmeticId: string
  slot: CosmeticSlot
  source: 'ledger' | 'purchase' | 'subscription' | 'title' | 'era_reward' | 'founder'
  /** The Era it was acquired in, or null for anything that predates Era scoping. */
  era: number | null
  acquiredAt: number
  equipped: boolean
}

/** The nine cosmetic slots. Read out of `cosmetic_entitlement`'s CHECK by the content gate. */
export type CosmeticSlot =
  | 'suit'
  | 'hat'
  | 'car'
  | 'watch'
  | 'trinket'
  | 'frame'
  | 'vault'
  | 'apartment'
  | 'office'

/**
 * The published spend cap, in cap units and in dollars.
 *
 * Both, deliberately. The UNITS are what the database enforces and what a support reply can be
 * checked against; the DOLLARS are what a player thinks in. Deriving one from the other on the
 * client would put the conversion in two places, and the whole reason cap units exist is that the
 * conversion is the thing that goes wrong.
 */
export interface CapView {
  spent: number
  limit: number
  remaining: number
  spentUsd: number
  limitUsd: number
  remainingUsd: number
}

/** One row of what this account has bought. The Ledger's money tab and the support answer. */
export interface PurchaseHistoryRow {
  id: string
  sku: string
  priceCents: number
  currency: string
  capUnits: number
  capUnitsRefunded: number
  refundedCents: number
  state: 'pending' | 'paid' | 'failed' | 'refunded' | 'chargeback' | 'refused_cap'
  era: number
  at: number
}

export interface PurchasePayload {
  cap: CapView
  history: PurchaseHistoryRow[]
}

/** What a completed checkout did. Returned by the landing page and by nothing else. */
export interface PurchaseCompletePayload {
  state: 'paid' | 'pending' | 'refused_cap' | 'failed' | 'cancelled'
  sku: string | null
  granted: string[]
  message: string
}

/* -------------------------------------------------------------------------- The Reveal */

/**
 * One action of this player's Era, with everything needed to check its roll and nothing else.
 *
 * `rollWord` is the ABSOLUTE index into the Era keystream and `words` is exactly what the server
 * drew. A reader recomputes `HMAC-SHA256(secret, "roll:e{era}:{playerId}:{floor(n/8)}")`, takes
 * the big-endian uint32 at byte `4 * (n mod 8)` for each `n` from `rollWord`, and compares. A row
 * that did not come off the stream carries `rollWord: -1` and declares itself rather than
 * quietly passing as one that did.
 */
export interface RevealRow {
  id: string
  kind: string
  refId: string
  outcome: string
  at: number
  rollWord: number
  words: number[]
  bustBp: number
}

export interface RevealPayload {
  era: number
  eraName: string
  state: EraState
  /** `SHA-256(ERA_SECRET)`, published at Era open, twelve weeks before the secret. */
  secretHash: string
  /**
   * `ERA_SECRET`. Null for the whole Era and a string forever after The Reveal.
   *
   * While it is null the rows below are still worth showing: they are the commitment's other
   * half, published continuously, so a player can see the exact list of things that will become
   * checkable rather than being asked to trust that a list exists.
   */
  secret: string | null
  /** The player's own id, which is the second term of the recipe. */
  playerId: string
  /** The published recipe, verbatim, so the screen never restates it in its own words. */
  recipe: string
  wordsPerBlock: number
  rows: RevealRow[]
  total: number
  /**
   * THE TIER MODIFIER, ON THE REPLAY. GAME-DESIGN 13.3 constraint 3, and the whole of it:
   *
   *   The `rng_seed` for every roll is still stored and every outcome is still reproducible from
   *   the ledger. If a player appeals, the replay tool shows exactly what happened, INCLUDING THE
   *   TIER MODIFIER. The game does not lie about its own dice.
   *
   * `shadowTier` is the rung and `shadowYield` is the multiplier that was applied to the payout.
   * Both are here even at tier 0, where the multiplier is exactly 1.0, because a field that
   * appears only when there is something to hide is a field whose presence IS the disclosure.
   *
   * The dice themselves are untouched: the multiplier lands on the payout after the outcome has
   * been decided, so the bust roll a player recomputes here is the roll the server used. That is
   * a deliberate reading of 13.3, which asks for "quietly degraded RNG and reduced yields" and
   * gets only the second half. DESIGN-DELTAS 1r.13 rules the first half out and says why.
   */
  shadowTier: 0 | 1 | 2 | 3
  shadowYield: number
}

/* -------------------------------------------------------------------------- Jest.com, Phase 26 */

/** Whether this deployment has a Jest game at all. Public. */
export interface JestStatus {
  configured: boolean
  gameId: string
}

/** A bearer session for the Jest build, minted from a `playerSigned` the Jest SDK produced. */
export interface JestLoginResult {
  token: string
  expiresAt: number
  playerId: string
  handle: string
  /** Era-scoped: a brand new PlayerDO was created by this request. */
  created: boolean
  /** Jest's word: a registered platform account, not a guest. */
  registered: boolean
}

/** One line per purchase Jest signed, in the order they arrived. */
export interface JestPurchaseResult {
  results: {
    purchaseToken: string
    sku: string | null
    result: 'settled' | 'already' | 'refused_cap' | 'refused'
    message: string
  }[]
}
